import { useEffect, useState, useRef, useCallback, memo, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiActivity, FiGlobe, FiSettings, FiUser, FiDownload, FiRefreshCw, FiUnlock, FiLock, FiClock, FiAlertTriangle, FiPlus, FiFlag, FiInfo, FiCheckCircle, FiEdit3, FiTrash2, FiSave, FiCreditCard, FiCalendar, FiArrowRight, FiCheck, FiX, FiStar, FiZap, FiShield, FiFileText, FiFilter, FiSearch } from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import { LineChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Seo from "../components/Seo";
import DarkSelect from "../components/DarkSelect";

export default function Dashboard({ serviceName }) {
	const canvasRef = useRef(null);
	const [metrics, setMetrics] = useState(null);
	const [suspiciousIPs, setSuspiciousIPs] = useState([]);
	const [blockedIPs, setBlockedIPs] = useState([]);
	const [activeTab, setActiveTab] = useState('overview');
	const [isLoading, setIsLoading] = useState(true);
	const [edgeStatus, setEdgeStatus] = useState({
		online: true,
		lastSync: '2 минуты назад',
		requests: 0,
		blocked: 0
	});

	const [domains, setDomains] = useState([]);
	const [selectedDomain, setSelectedDomain] = useState(null);
	const [showAddDomainModal, setShowAddDomainModal] = useState(false);
	const [showVerificationModal, setShowVerificationModal] = useState(false);
	const [verificationData, setVerificationData] = useState({
		domain: '',
		domainId: null,
		txtRecord: ''
	});
	const [newDomain, setNewDomain] = useState({ name: '', backendIP: '' });
	const [domainConfig, setDomainConfig] = useState(null);
	const [requestsLoading, setRequestsLoading] = useState(true);
	const [trafficData, setTrafficData] = useState([]);
	const [geoData, setGeoData] = useState([]);

	const [archivePeriod, setArchivePeriod] = useState('daily'); // hourly, daily, monthly
	const [archiveRange, setArchiveRange] = useState({ from: '', to: '' });
	const [archiveMetrics, setArchiveMetrics] = useState(null);
	const [archiveTraffic, setArchiveTraffic] = useState([]);
	const [archiveGeo, setArchiveGeo] = useState([]);
	const [archiveLoading, setArchiveLoading] = useState(false);
	const [aiData, setAiData] = useState(null);
	const [aiLoading, setAiLoading] = useState(false);
	const [domainSearch, setDomainSearch] = useState('');

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;

		const particles = Array.from({ length: 150 }).map(() => ({
			x: Math.random() * canvas.width,
			y: Math.random() * canvas.height,
			dx: (Math.random() - 0.5) * 1.2,
			dy: (Math.random() - 0.5) * 1.2,
			size: Math.random() * 3 + 1,
			alpha: Math.random() * 0.5 + 0.1
		}));

		let animationId;
		const animate = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			for (let i = 0; i < particles.length; i++) {
				for (let j = i + 1; j < particles.length; j++) {
					const dx = particles[i].x - particles[j].x;
					const dy = particles[i].y - particles[j].y;
					const distance = Math.sqrt(dx * dx + dy * dy);

					if (distance < 120) {
						ctx.strokeStyle = `rgba(100, 100, 255, ${0.2 - distance / 600})`;
						ctx.lineWidth = 0.8;
						ctx.beginPath();
						ctx.moveTo(particles[i].x, particles[i].y);
						ctx.lineTo(particles[j].x, particles[j].y);
						ctx.stroke();
					}
				}
			}

			particles.forEach(p => {
				p.x += p.dx;
				p.y += p.dy;

				if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
				if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

				ctx.fillStyle = `rgba(100, 150, 255, ${p.alpha})`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
				ctx.fill();
			});

			animationId = requestAnimationFrame(animate);
		};

		animate();
		return () => cancelAnimationFrame(animationId);
	}, []);

	const loadDomainConfig = async () => {
		try {
			const res = await fetch(`/api/domain-config?domain=${selectedDomain}`);
			const config = await res.json();
			setDomainConfig(config);
		} catch (err) {
			console.error("Ошибка загрузки конфигурации:", err);
		}
	};

	const updateDomainConfig = useCallback(async (newConfig) => {
		try {
			const res = await fetch('/api/ui/configuration/domain-config/update', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ domain_id: selectedDomain, config: newConfig })
			});
			if (res.ok) {
				setDomainConfig(newConfig);
			}
		} catch (err) {
			console.error("Ошибка обновления конфигурации:", err);
		}
	}, [selectedDomain]);

	const loadOverviewData = useCallback(async () => {
		if (!selectedDomain) return;

		setIsLoading(true);
		setRequestsLoading(true);

		try {
			const [
				metricsRes,
				trafficRes,
				geoRes
			] = await Promise.all([
				fetch(`/api/metrics?domain=${selectedDomain}`),
				fetch(`/api/traffic-data?domain=${selectedDomain}`),
				fetch(`/api/geo-data?domain=${selectedDomain}`)
			]);

			setMetrics(await metricsRes.json());

			const trafficDataJson = await trafficRes.json();
			const geoDataJson = await geoRes.json();

			setTrafficData(Array.isArray(trafficDataJson) ? trafficDataJson : []);
			setGeoData(Array.isArray(geoDataJson) ? geoDataJson : []);

			// Загружаем domainConfig для вкладки "Обзор" (реже, раз в 10 минут)
			if (activeTab === 'overview') {
				await loadDomainConfig();
			}
		} catch (err) {
			console.error("Ошибка загрузки данных:", err);
			setTrafficData([]);
			setGeoData([]);
		}

		setIsLoading(false);
		setRequestsLoading(false);
	}, [selectedDomain, activeTab]);

	const loadArchiveData = useCallback(async () => {
		if (!selectedDomain) return;
		setArchiveLoading(true);
		try {
			const params = new URLSearchParams({
				domain: selectedDomain,
				period: archivePeriod,
			});
			if (archiveRange.from) params.append('from', archiveRange.from);
			if (archiveRange.to) params.append('to', archiveRange.to);

			const res = await fetch(`/api/archive?${params}`);
			const json = await res.json();

			if (json.status === 'success' && json.data) {
				const { traffic, geo, totals } = json.data;
				setArchiveTraffic(Array.isArray(traffic) ? traffic : []);
				setArchiveGeo(Array.isArray(geo) ? geo : []);
				setArchiveMetrics({
					totalRequests: totals?.requests ?? 0,
					blockedRequests: totals?.blocked ?? 0,
					responseTime: totals?.responseTime ?? null,
				});
			} else {
				// Обработка ошибки или пустого ответа
				setArchiveMetrics(null);
				setArchiveTraffic([]);
				setArchiveGeo([]);
			}
		} catch (err) {
			console.error('Ошибка загрузки архива:', err);
			setArchiveMetrics(null);
			setArchiveTraffic([]);
			setArchiveGeo([]);
		}
		setArchiveLoading(false);
	}, [selectedDomain, archivePeriod, archiveRange]);

	const loadAIData = useCallback(async () => {
		if (!selectedDomain) return;
		setAiLoading(true);
		try {
			const params = new URLSearchParams({
				domain: selectedDomain,
				period: archivePeriod,
			});
			if (archiveRange.from) params.append('from', archiveRange.from);
			if (archiveRange.to) params.append('to', archiveRange.to);

			const res = await fetch(`/api/archive/ai?${params}`);
			const json = await res.json();

			if (json.type === 'success') {
				setAiData(json.data);
			} else {
				setAiData(null);
			}
		} catch (err) {
			console.error('Ошибка загрузки AI статистики:', err);
			setAiData(null);
		}
		setAiLoading(false);
	}, [selectedDomain, archivePeriod, archiveRange]);

	useEffect(() => {
		if (activeTab === 'configuration') {
			loadDomainConfig();
			const interval = setInterval(() => {
				loadDomainConfig();
			}, 600000);

			return () => clearInterval(interval);
		}
		if (activeTab === 'protection') {
			loadDomainConfig();
			const interval = setInterval(() => {
				loadDomainConfig();
			}, 600000);

			return () => clearInterval(interval);
		}
	}, [activeTab, selectedDomain]);

	useEffect(() => {
		if (!selectedDomain || activeTab !== 'overview') return;

		loadOverviewData();
		// 30 секунд для вкладки обзор
		const interval = setInterval(loadOverviewData, 30000);
		return () => clearInterval(interval);
	}, [selectedDomain, activeTab, loadOverviewData]);

	useEffect(() => {
		if (activeTab === 'archive' && selectedDomain) {
			loadArchiveData();
		}
	}, [activeTab, selectedDomain, archivePeriod]);

	useEffect(() => {
		if (activeTab === 'archive' && selectedDomain) {
			loadAIData();
		}
	}, [activeTab, selectedDomain, archivePeriod]);

	const handleUnblockIP = useCallback(async (ip) => {
		try {
			await fetch('/api/unblock-ip', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ip, domain: selectedDomain })
			});
			setBlockedIPs(prev => prev.filter(item => item.ip !== ip));
		} catch (err) {
			console.error("Ошибка разблокировки IP:", err);
		}
	}, [selectedDomain]);

	const handleBlockIP = useCallback(async (ip) => {
		try {
			await fetch('/api/block-ip', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ip, domain: selectedDomain })
			});
			setBlockedIPs(prev => [...prev, { ip, timestamp: new Date().toISOString(), reason: 'Ручная блокировка' }]);
		} catch (err) {
			console.error("Ошибка блокировки IP:", err);
		}
	}, [selectedDomain]);

	const refreshCurrentTab = async () => {
		if (activeTab === 'overview') {
			await loadOverviewData();
		} else if (activeTab === 'protection' || activeTab === 'configuration') {
			await loadDomainConfig();
		} else if (activeTab === 'account') {
			await refreshDomains();
		}
	};

	useEffect(() => {
		const loadDomains = async () => {
			try {
				const res = await fetch('/api/my-domains', {
					method: 'GET',
					credentials: 'include',
				});
				if (!res.ok) throw new Error("Ошибка загрузки доменов");

				const result = await res.json();
				const domainsData = result.data || [];

				setDomains(domainsData);
				if (domainsData.length > 0) {
					setSelectedDomain(domainsData[0].id);
				}
			} catch (err) {
				console.error("Ошибка получения доменов:", err);
			}
		};
		loadDomains();
	}, []);

	const refreshDomains = async () => {
		try {
			const res = await fetch('/api/my-domains', {
				method: 'GET',
				credentials: 'include',
			});

			if (res.ok) {
				const result = await res.json();
				const domainsData = result.data || [];
				setDomains(domainsData);

				// Если выбранный домен был удален, сбросить выбор
				if (selectedDomain && !domainsData.find(d => d.id === selectedDomain)) {
					setSelectedDomain(domainsData.length > 0 ? domainsData[0].id : null);
				}
			}
		} catch (err) {
			console.error("Ошибка обновления доменов:", err);
		}
	};

	useEffect(() => {
		const interval = setInterval(refreshDomains, 5000);
		return () => clearInterval(interval);
	}, []);

	const handleAddDomain = async () => {
		try {
			const res = await fetch('/api/add-domain', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(newDomain)
			});

			if (res.ok) {
				const response = await res.json();

				const domainData = response.data;

				const updatedDomains = [...domains, { ...domainData, verified: false }];
				setDomains(updatedDomains);
				setShowAddDomainModal(false);
				setNewDomain({ name: '', backendIP: '' });

				setVerificationData({
					domain: domainData.name,
					domainId: domainData.id,
					txtRecord: domainData.verificationTxt || `ai-shield-verification=${domainData.id}`,
					isVerified: false
				});
				setShowVerificationModal(true);
			}
		} catch (err) {
			console.error("Ошибка добавления домена:", err);
		}
	};

	const handleVerifyDomain = (domainId) => {
		const domainToVerify = domains.find(d => d.id === domainId);
		if (domainToVerify && !domainToVerify.verified) {
			setVerificationData({
				domain: domainToVerify.name,
				domainId: domainToVerify.id,
				txtRecord: domainToVerify.verificationTxt || `ai-shield-verification=${domainToVerify.id}`
			});
			setDomains(domains.map(d =>
				d.id === domainId ? { ...d, verified: true } : d
			));
			setShowVerificationModal(false);
		}
	};

	const handleDeleteDomain = async (domainId) => {
		try {
			const res = await fetch('/api/delete-domain', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: domainId })
			});

			if (res.ok) {
				setDomains(domains.filter(d => d.id !== domainId));
				if (selectedDomain === domainId) {
					setSelectedDomain(domains.length > 1 ? domains[0].id : null);
				}
			}
		} catch (err) {
			console.error("Ошибка удаления домена:", err);
		}
	};

	const filteredDomains = useMemo(() => {
		if (!domainSearch.trim()) return domains;
		const search = domainSearch.toLowerCase();
		return domains.filter(d => d.name.toLowerCase().includes(search));
	}, [domains, domainSearch]);

	return (
		<>
			<Seo
				title={`${serviceName} - Личный кабинет`}
				description="Умная защита от DDoS атак, ботов и кибератак."
			/>
			<div className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
				<canvas ref={canvasRef} className="absolute inset-0 z-0" />
				<div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-purple-900/30 to-black/70" />

				<main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-6">
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
						>
							<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
								{serviceName} Личный кабинет
							</h1>
							<p className="text-gray-400 mt-2">Мониторинг и управление защитой ваших ресурсов</p>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="flex items-center gap-4"
						>
							<button
								onClick={refreshCurrentTab}
								disabled={isLoading}
								className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
							>
								<FiRefreshCw className={`${isLoading ? 'animate-spin' : ''}`} />
								Обновить
							</button>
						</motion.div>
					</header>

					<div className="mb-6 flex flex-wrap items-center gap-4">
						<label className="text-gray-400">Домен:</label>
						<div className="relative flex-1 min-w-[200px] max-w-xs">
							<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
							<input
								type="text"
								placeholder="Поиск домена..."
								value={domainSearch}
								onChange={e => setDomainSearch(e.target.value)}
								className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
							/>
						</div>
						<div className="flex flex-wrap gap-2 items-center">
							{filteredDomains.map(domain => (
								<button
									key={domain.id}
									onClick={() => setSelectedDomain(domain.id)}
									className={`px-4 py-2 rounded-lg transition-all ${selectedDomain === domain.id
										? 'bg-blue-600 text-white'
										: 'bg-white/5 hover:bg-white/10'}`}
								>
									{domain.name}
									{!domain.verified && (
										<span className="ml-2 text-xs bg-yellow-500 text-yellow-900 px-2 py-1 rounded-full">
											Ожидает проверки
										</span>
									)}
								</button>
							))}
							{filteredDomains.length === 0 && domains.length > 0 && (
								<span className="text-gray-500 text-sm">Ничего не найдено</span>
							)}
							<button
								onClick={() => setShowAddDomainModal(true)}
								className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-all"
							>
								<FiPlus />
								Добавить домен
							</button>
						</div>
					</div>

					<nav className="flex overflow-x-auto pb-2 mb-8 scrollbar-hide">
						<div className="flex space-x-1 bg-white/5 rounded-xl p-1">
							{[
								{ id: 'overview', icon: <FiActivity />, label: 'Обзор' },
								{ id: 'protection', icon: <FiShield />, label: 'Защита' },
								{ id: 'configuration', icon: <FiSettings />, label: 'Конфигурация' },
								{ id: 'logs', icon: <FiFileText />, label: 'Логи' },
								{ id: 'archive', icon: <FiClock />, label: 'Архив' },
								{ id: 'account', icon: <FiUser />, label: 'Аккаунт' }
							].map((tab) => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`flex items-center px-4 py-2 rounded-lg transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'hover:bg-white/10'}`}
								>
									<span className="mr-2">{tab.icon}</span>
									{tab.label}
								</button>
							))}
						</div>
					</nav>

					<div className="mb-16">
						<AnimatePresence mode="wait">
							<motion.div
								key={activeTab}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.2 }}
							>
								{activeTab === 'overview' && (
									<OverviewTab
										metrics={metrics}
										edgeStatus={edgeStatus}
										isLoading={isLoading}
										suspiciousIPs={suspiciousIPs}
										selectedDomain={selectedDomain}
										selectedDomainName={domains.find(d => d.id === selectedDomain)?.name}
										trafficData={trafficData}
										geoData={geoData}
										onAddDomain={() => setShowAddDomainModal(true)}
									/>
								)}

								{activeTab === 'protection' && (
									<ProtectionTab
										domainConfig={domainConfig}
										onUpdateConfig={updateDomainConfig}
										blockedIPs={blockedIPs}
										suspiciousIPs={suspiciousIPs}
										//blockedRequests={blockedRequests}
										onUnblock={handleUnblockIP}
										onBlock={handleBlockIP}
										isLoading={isLoading}
										requestsLoadig={requestsLoading}
										selectedDomain={selectedDomain}
										onAddDomain={() => setShowAddDomainModal(true)}
									/>
								)}

								{activeTab === 'configuration' && (
									<ConfigurationTab
										domainConfig={domainConfig}
										onUpdateConfig={updateDomainConfig}
										isLoading={isLoading}
										selectedDomain={selectedDomain}
										onAddDomain={() => setShowAddDomainModal(true)}
									/>
								)}

								{activeTab === 'logs' && (
									<LogsTab
										selectedDomain={selectedDomain}
										selectedDomainName={domains.find(d => d.id === selectedDomain)?.name}
										onAddDomain={() => setShowAddDomainModal(true)}
									/>

								)}

								{activeTab === 'archive' && (
									<ArchiveTab
										selectedDomain={selectedDomain}
										selectedDomainName={domains.find(d => d.id === selectedDomain)?.name}
										onAddDomain={() => setShowAddDomainModal(true)}
										archiveMetrics={archiveMetrics}
										archiveTraffic={archiveTraffic}
										archiveGeo={archiveGeo}
										archiveLoading={archiveLoading}
										archivePeriod={archivePeriod}
										setArchivePeriod={setArchivePeriod}
										archiveRange={archiveRange}
										setArchiveRange={setArchiveRange}
										onLoadArchive={loadArchiveData}
										userRate={domainConfig?.rate}
										aiData={aiData}
										aiLoading={aiLoading}
										onLoadAI={loadAIData}
									/>
								)}

								{activeTab === 'account' && (
									<AccountTab
										domains={domains}
										onAddDomain={() => setShowAddDomainModal(true)}
										onVerifyDomain={handleVerifyDomain}
										onDeleteDomain={handleDeleteDomain}
										onRefreshDomains={refreshDomains}
									/>
								)}
							</motion.div>
						</AnimatePresence>
					</div>
				</main>

				<AnimatePresence>
					{showAddDomainModal && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
							onClick={() => setShowAddDomainModal(false)}
						>
							<motion.div
								initial={{ scale: 0.9, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0.9, opacity: 0 }}
								className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
								onClick={e => e.stopPropagation()}
							>
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-xl font-semibold">Добавление домена</h3>
									<button
										onClick={() => setShowAddDomainModal(false)}
										className="p-1 hover:bg-white/10 rounded-lg"
									>
										<FiX size={24} />
									</button>
								</div>

								<div className="space-y-4">
									<div>
										<label className="block text-sm font-medium text-gray-400 mb-1">Доменное имя</label>
										<input
											type="text"
											placeholder="example.com"
											className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
											value={newDomain.name}
											onChange={e => setNewDomain({ ...newDomain, name: e.target.value })}
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-400 mb-1">IP адрес бекенда</label>
										<input
											type="text"
											placeholder="192.168.1.100:8080"
											className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
											value={newDomain.backendIP}
											onChange={e => setNewDomain({ ...newDomain, backendIP: e.target.value })}
										/>
									</div>

									{/* Добавленная ссылка на документацию */}
									<div className="text-sm text-gray-400">
										<a
											href="/docs#setup"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
										>
											<FiInfo className="w-4 h-4" />
											Как правильно подключить домен?
										</a>
									</div>

									<button
										onClick={handleAddDomain}
										disabled={!newDomain.name || !newDomain.backendIP}
										className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg transition-all"
									>
										Добавить домен
									</button>
								</div>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>
				<VerificationModal
					isOpen={showVerificationModal}
					onClose={() => setShowVerificationModal(false)}
					domain={verificationData?.domain || ''}
					domainId={verificationData?.domainId || null}
					txtRecord={verificationData?.txtRecord || ''}
					onVerifyDomain={handleVerifyDomain}
					isDomainVerified={verificationData?.isVerified || false} // Добавьте этот пропс
					balancerIP="123.45.67.89" // Замените на реальный IP вашего прокси-сервера
					balancerCNAME="proxy.yourdomain.com" // Замените на ваш CNAM 
				/>
			</div>
		</>
	);
}

const NoDomainsMessage = ({ onAddDomain }) => {
	return (
		<div className="flex flex-col items-center justify-center py-16 text-center">
			<div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mb-6">
				<FiGlobe className="w-12 h-12" />
			</div>
			<h3 className="text-2xl font-bold mb-4">Добавьте свой первый домен</h3>
			<p className="text-gray-300 max-w-md mb-8">
				Здесь будет отображаться статистика трафика, защита и настройки вашего сайта
				после добавления и подключения домена.
			</p>
			<div className="flex flex-col sm:flex-row gap-4">
				<button
					onClick={onAddDomain}
					className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-blue-500 rounded-xl font-semibold transition-all duration-300 hover:scale-105 flex items-center gap-2"
				>
					<FiPlus className="w-5 h-5" />
					Добавить домен
				</button>
				<a
					href="/docs#setup"
					className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2"
				>
					<FiArrowRight className="w-5 h-5" />
					Инструкция по подключению
				</a>
			</div>
		</div>
	);
};

const VerificationModal = ({
	isOpen,
	onClose,
	domain,
	domainId,
	txtRecord,
	onVerifyDomain
}) => {
	const [isCheckingVerification, setIsCheckingVerification] = useState(false);
	const [verificationStatus, setVerificationStatus] = useState(null);

	const handleCheckVerification = async () => {
		if (!domainId) return;

		setIsCheckingVerification(true);
		try {
			const res = await fetch(`/api/check-domain-verify?id=${domainId}`);
			const data = await res.json();

			const isVerified = data.data?.verified;

			if (isVerified) {
				setVerificationStatus('success');
				onVerifyDomain(domainId);
			} else {
				setVerificationStatus('pending');
			}
		} catch (err) {
			console.error("Ошибка проверки верификации:", err);
			setVerificationStatus('error');
		}
		setIsCheckingVerification(false);
	};

	const handleClose = () => {
		setVerificationStatus(null);
		onClose();
	};

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text || '');
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
					onClick={handleClose}
				>
					<motion.div
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.9, opacity: 0 }}
						className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
						onClick={e => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-xl font-semibold">Подтверждение домена</h3>
							<button
								onClick={handleClose}
								className="p-1 hover:bg-white/10 rounded-lg"
							>
								<FiX size={24} />
							</button>
						</div>

						<div className="space-y-4">
							<div className="bg-blue-900/30 p-4 rounded-lg text-sm">
								<h4 className="font-medium mb-2">Инструкция по проверке:</h4>
								<p className="mb-2">Для подтверждения владения доменом <strong>{domain}</strong> добавьте TXT запись:</p>
								<code className="block bg-black/30 p-2 rounded mb-2 font-mono text-sm break-all">
									{txtRecord}
								</code>
								<p className="text-xs text-gray-400 mb-2">
									Добавьте эту запись в DNS настройках вашего домена через панель управления хостингом.
								</p>
								{/* Добавленная ссылка на документацию */}
								<div className="mt-3 pt-3 border-t border-white/10">
									<a
										href="/docs#setup"
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
									>
										<FiArrowRight className="w-3 h-3" />
										Подробная инструкция по подключению домена
									</a>
								</div>
								<p>После добавления записи нажмите кнопку "Проверить" для подтверждения.</p>
							</div>

							{verificationStatus === 'success' && (
								<div className="bg-green-500/20 border border-green-500/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 text-green-400">
										<FiCheckCircle size={20} />
										<span className="font-medium">Домен успешно подтвержден!</span>
									</div>
									<p className="text-sm text-green-400 mt-1">
										Теперь вам нужно настроить DNS записи для направления трафика через наш прокси-сервер.
									</p>
								</div>
							)}

							{verificationStatus === 'pending' && (
								<div className="bg-yellow-500/20 border border-yellow-500/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 text-yellow-400">
										<FiAlertTriangle size={20} />
										<span className="font-medium">Ожидание подтверждения</span>
									</div>
									<p className="text-sm text-yellow-400 mt-1">
										TXT запись еще не обнаружена или DNS изменения не вступили в силу.
										Пожалуйста, подождите некоторое время и попробуйте снова.
									</p>
								</div>
							)}

							{verificationStatus === 'error' && (
								<div className="bg-red-500/20 border border-red-500/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 text-red-400">
										<FiX size={20} />
										<span className="font-medium">Ошибка проверки</span>
									</div>
									<p className="text-sm text-red-400 mt-1">
										Произошла ошибка при проверке домена. Попробуйте позже.
									</p>
								</div>
							)}

							<div className="flex gap-3">
								<button
									onClick={() => copyToClipboard(txtRecord)}
									className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
								>
									Скопировать
								</button>
								<button
									onClick={handleCheckVerification}
									disabled={isCheckingVerification || !domainId}
									className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg transition-all flex items-center justify-center gap-2"
								>
									{isCheckingVerification ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
											Проверка...
										</>
									) : (
										<>
											<FiRefreshCw size={16} />
											Проверить
										</>
									)}
								</button>
							</div>

							<div className="text-xs text-gray-400 text-center">
								Обычно DNS изменения вступают в силу в течение нескольких минут,
								но иногда это может занять до 24 часов.
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

const ProxySetupModal = ({
	isOpen,
	onClose,
	domain,
	nodes = [],          // массив IP
	acmeCNAME = '',      // целевой CNAME
	isLoading = false
}) => {
	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text || '');
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
					onClick={onClose}
				>
					<motion.div
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.9, opacity: 0 }}
						className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
						onClick={e => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-xl font-semibold">Настройка прокси</h3>
							<button
								onClick={onClose}
								className="p-1 hover:bg-white/10 rounded-lg"
								disabled={isLoading}
							>
								<FiX size={24} />
							</button>
						</div>

						{isLoading ? (
							<div className="flex justify-center py-8">
								<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
							</div>
						) : (
							<div className="space-y-4">
								<div className="bg-purple-900/30 p-4 rounded-lg text-sm">
									<h4 className="font-medium mb-2">Настройка прокси-сервера</h4>
									<p className="mb-4">
										Для защиты вашего домена <strong>{domain}</strong> направьте трафик через наши прокси-серверы.
									</p>

									{/* Список A-записей */}
									{nodes && nodes.length > 0 && (
										<div className="mb-4">
											<h5 className="font-medium mb-2">A-записи</h5>
											<p className="mb-2 text-xs text-gray-400">
												Установите A-записи для вашего домена на следующие IP-адреса:
											</p>
											<ul className="space-y-2">
												{nodes.map((ip, idx) => (
													<li key={idx}
														className="flex items-center justify-between bg-black/30 p-2 rounded">
														<span className="font-mono">{ip}</span>
														<button
															onClick={() => copyToClipboard(ip)}
															className="ml-2 text-blue-400 hover:text-blue-300"
														>
															Копировать
														</button>

													</li>
												))}
											</ul>
										</div>
									)}


									{/* CNAME для ACME */}
									{acmeCNAME && (

										<div className="mb-4">
											<h5 className="font-medium mb-2">CNAME запись</h5>
											<p className="mb-2 text-xs text-gray-400">
												Создайте CNAME запись для поддомена{' '}
												<code className="bg-black/20 px-1 rounded">_acme-challenge.{domain}</code>{' '}
												со значением:

											</p>

											<div className="flex items-center justify-between bg-black/30 p-2 rounded">
												<span className="font-mono">{acmeCNAME}</span>
												<button
													onClick={() => copyToClipboard(acmeCNAME)}
													className="ml-2 text-blue-400 hover:text-blue-300"

												>
													Копировать
												</button>
											</div>
										</div>
									)}

									<div className="mt-4 bg-green-900/20 border border-green-500/30 p-3 rounded-md">
										<p className="text-green-400 text-sm font-medium mb-1">💡 Важная информация:</p>
										<p className="text-green-400 text-xs">
											После изменения DNS записей может потребоваться до 24 часов для распространения изменений.
											Система автоматически определит, когда трафик начнет поступать через наш прокси-сервер,
											и обновит статус домена на "Активен".
										</p>
									</div>
									{/* Добавленная ссылка на документацию */}
									<div className="mt-4 pt-4 border-t border-white/10">
										<a
											href="/docs#setup"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
										>
											<FiArrowRight className="w-3 h-3" />
											Подробная инструкция по настройке DNS в документации
										</a>
									</div>
								</div>

								<button
									onClick={onClose}
									className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
								>
									Готово
								</button>

								<div className="text-xs text-gray-400 text-center">
									После настройки DNS мониторинг трафика начнется автоматически.
								</div>
							</div>
						)}
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

// Компонент для графика трафика
const TrafficChart = memo(({ data }) => {
	if (!Array.isArray(data)) {
		console.warn("TrafficChart получил не массив:", data);
		return <div className="text-gray-400">Нет данных для отображения</div>;
	}

	if (data.length === 0) {
		return <div className="text-gray-400">Нет данных для отображения</div>;
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<LineChart
				data={data}
				margin={{ top: 5, right: 20, left: 20, bottom: 20 }} // Добавляем отступы
			>
				<CartesianGrid strokeDasharray="3 3" stroke="#374151" />
				<XAxis
					dataKey="time"
					stroke="#9CA3AF"
					tick={{ fontSize: 12 }}
					interval="preserveStartEnd" // Равномерное распределение меток
				/>
				<YAxis
					stroke="#9CA3AF"
					tick={{ fontSize: 12 }}
					width={60} // Фиксируем ширину для Y оси
				/>
				<Tooltip
					contentStyle={{
						backgroundColor: "#1F2937",
						borderColor: "#374151",
						color: "#FFF",
						fontSize: "14px",
					}}
				/>
				<Legend
					wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
				/>
				<Line
					type="monotone"
					dataKey="requests"
					stroke="#3B82F6"
					strokeWidth={2}
					dot={false}
					activeDot={{ r: 4 }}
				/>
				<Line
					type="monotone"
					dataKey="blocked"
					stroke="#EF4444"
					strokeWidth={2}
					dot={false}
				/>
			</LineChart>
		</ResponsiveContainer>
	);
});

// Компонент для карты географического распределения
const GeoChart = memo(({ data }) => {
	const COLORS = [
		"#3B82F6",
		"#60A5FA",
		"#93C5FD",
		"#BFDBFE",
		"#DC2626",
		"#EF4444",
		"#F87171",
		"#FECACA",
	];

	if (!Array.isArray(data)) {
		console.warn("GeoChart получил не массив:", data);
		return <div className="text-gray-400">Нет данных для отображения</div>;
	}

	if (data.length === 0) {
		return <div className="text-gray-400">Нет данных для отображения</div>;
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data}
				layout="vertical"
				margin={{ top: 5, right: 20, left: 0, bottom: 20 }} // Настраиваем отступы
				barSize={20} // Фиксируем размер столбцов
			>
				<CartesianGrid strokeDasharray="3 3" stroke="#374151" />
				<XAxis
					type="number"
					stroke="#9CA3AF"
					tick={{ fontSize: 12 }}
				/>
				<YAxis
					dataKey="country"
					type="category"
					stroke="#9CA3AF"
					tick={{ fontSize: 12 }}
					width={80} // Фиксируем ширину для названий стран
				/>
				<Tooltip
					contentStyle={{
						backgroundColor: "#1F2937",
						borderColor: "#374151",
						color: "#FFF",
						fontSize: "14px",
					}}
				/>
				<Bar
					dataKey="requests"
					radius={[0, 4, 4, 0]} // Закругляем углы столбцов
				>
					{data.map((entry, index) => (
						<Cell
							key={`cell-${index}`}
							fill={COLORS[index % COLORS.length]}
						/>
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
});

function OverviewTab({ metrics, edgeStatus, isLoading, suspiciousIPs, selectedDomain, selectedDomainName, trafficData, geoData, onAddDomain }) {
	if (!selectedDomain) {
		return <NoDomainsMessage onAddDomain={onAddDomain} />;
	}
	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-semibold">
					Обзор защиты для <span className="text-blue-400">{selectedDomainName}</span>
				</h2>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
				<StatCard
					title="Всего запросов"
					value={metrics?.totalRequests || 0}
					change={metrics?.requestChange || 0}
					icon={<FiActivity className="text-blue-400" />}
					loading={isLoading}
				/>
				<StatCard
					title="Блокировано"
					value={metrics?.blockedRequests || 0}
					change={metrics?.blockedChange || 0}
					icon={<FiShield className="text-purple-400" />}
					loading={isLoading}
				/>
				<StatCard
					title="Подозрительных IP"
					value={metrics?.suspiciousIPs || 0}
					change={metrics?.suspiciousChange || 0}
					icon={<FiAlertTriangle className="text-yellow-400" />}
					loading={isLoading}
				/>
				<StatCard
					title="Время ответа"
					value={`${metrics?.responseTime || 0}мс`}
					change={metrics?.responseTimeChange || 0}
					icon={<FiGlobe className="text-green-400" />}
					loading={isLoading}
				/>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">Трафик (запросы/сек)</h3>
					<div className="h-[calc(100%-2rem)]"> {/* Контейнер с фиксированной высотой */}
						{trafficData.length > 0 ? (
							<TrafficChart data={trafficData} />
						) : (
							<div className="flex items-center justify-center h-full">
								<div className="text-gray-400">Загрузка данных...</div>
							</div>
						)}
					</div>
				</div>

				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">География атак</h3>
					<div className="h-[calc(100%-2rem)]"> {/* Контейнер с фиксированной высотой */}
						{geoData.length > 0 ? (
							<GeoChart data={geoData} />
						) : (
							<div className="flex items-center justify-center h-full">
								<div className="text-gray-400">Загрузка данных...</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

const FeatureBlock = memo(({ children, feature, isFeatureAvailable }) => {
	const available = isFeatureAvailable(feature);

	return (
		<div className={`relative ${!available ? 'opacity-50 pointer-events-none' : ''}`}>
			{!available && (
				<div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl z-10">
					<span className="text-white font-semibold bg-black/70 px-4 py-2 rounded-lg">
						Недоступно в вашем тарифном плане
					</span>
				</div>
			)}
			{children}
		</div>
	);
});

const formatTimeRemaining = (expiresIn) => {
	if (expiresIn <= 0) return 'Истекло';
	const hours = Math.floor(expiresIn / 3600);
	const minutes = Math.floor((expiresIn % 3600) / 60);
	const seconds = expiresIn % 60;
	if (hours > 0) return `${hours}ч ${minutes}м ${seconds}с`;
	if (minutes > 0) return `${minutes}м ${seconds}с`;
	return `${seconds}с`;
};

const Timer = memo(({ initialExpiresIn, onExpire }) => {
	const [timeLeft, setTimeLeft] = useState(initialExpiresIn);

	useEffect(() => {
		const interval = setInterval(() => {
			setTimeLeft(prev => {
				if (prev <= 1) {
					clearInterval(interval);
					onExpire?.();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(interval);
	}, [onExpire]);

	return <span>{formatTimeRemaining(timeLeft)}</span>;
});

const ProtectionTab = memo(function ProtectionTab({
	selectedDomain,
	domainConfig,
	blockedIPs,
	suspiciousIPs,
	onUnblock,
	onBlock,
	isLoading,
	requestsLoading,
	onUpdateConfig,
	onAddDomain
}) {
	const [newIP, setNewIP] = useState('');
	const [ipDetails, setIpDetails] = useState(null);
	const [isLoadingIPDetails, setIsLoadingIPDetails] = useState(false);
	const [selectedIP, setSelectedIP] = useState(null);
	const [showIPDetailsModal, setShowIPDetailsModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedThreat, setSelectedThreat] = useState('all');
	const [showHighSeverityOnly, setShowHighSeverityOnly] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState(null);
	const [showRequestDetailsModal, setShowRequestDetailsModal] = useState(false);
	const [blockedRequestsLimit, setBlockedRequestsLimit] = useState(20);
	const [blockedRequestsReason, setBlockedRequestsReason] = useState('all');
	const [blockedRequests, setBlockedRequests] = useState([]);
	const [isRefreshingBlocked, setIsRefreshingBlocked] = useState(false);
	const [ipSearch, setIpSearch] = useState('');
	const [debouncedIpSearch, setDebouncedIpSearch] = useState('');
	const [timedIPs, setTimedIPs] = useState([]);

	const isFeatureAvailable = useCallback((feature) => {
		const rate = domainConfig?.rate || '';
		if (!rate) return false;
		if (rate.startsWith("Test")) return true;
		if (rate.startsWith("Base")) return feature === 'bots';
		if (rate.startsWith("Pro")) return feature !== 'ai_sensitivity';
		if (rate.startsWith("Full")) return true;
		return false;
	}, [domainConfig?.rate]);

	const isIPManagementAvailable = () => {
		const rate = domainConfig?.rate || '';

		if (!rate) return false;

		if (rate.startsWith("Test")) return true;
		if (rate.startsWith("Pro")) return true;
		if (rate.startsWith("Full")) return true;

		return false;
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedIpSearch(ipSearch);
		}, 300);
		return () => clearTimeout(timer);
	}, [ipSearch]);

	const fetchBlockedRequests = useCallback(async () => {
		if (!domainConfig?.rate) return;

		try {
			const params = new URLSearchParams({
				domain: selectedDomain,
				limit: blockedRequestsLimit,
				reason: blockedRequestsReason,
			});
			if (ipSearch.trim()) params.append('ip', ipSearch.trim());

			const response = await fetch(`/api/blocked-requests?${params}`);
			const data = await response.json();
			setBlockedRequests(data);
		} catch (error) {
			console.error("Ошибка загрузки заблокированных запросов:", error);
		}
	}, [selectedDomain, blockedRequestsLimit, blockedRequestsReason, ipSearch]);

	const refreshBlockedRequests = async () => {
		if (!domainConfig?.rate) return;

		setIsRefreshingBlocked(true);
		try {
			await fetchBlockedRequests();
		} finally {
			setIsRefreshingBlocked(false);
		}
	};

	useEffect(() => {
		if (selectedDomain) {
			fetchBlockedRequests();
		}
	}, [selectedDomain, blockedRequestsLimit, blockedRequestsReason, ipSearch, fetchBlockedRequests]);

	const handleAddIP = (ip = null, listType = null) => {
		if (!isIPManagementAvailable()) return;

		const ipToAdd = ip || newIP;
		const targetListType = listType;

		if (!ipToAdd || !isValidIP(ipToAdd)) return;

		const updatedConfig = { ...domainConfig };
		if (targetListType === 'white_list') {
			updatedConfig.white_list = [...(updatedConfig.white_list || []), ipToAdd];
		} else {
			updatedConfig.blocked_ips = updatedConfig.blocked_ips || { temporary: {}, permanent: [] };
			updatedConfig.blocked_ips.permanent = [...(updatedConfig.blocked_ips.permanent || []), ipToAdd];
		}

		onUpdateConfig(updatedConfig);
		if (!ip) setNewIP(''); // Сбрасываем только если добавляли через форму
	};

	const fetchIPDetails = async (ip) => {
		if (!domainConfig?.rate) return;

		setIsLoadingIPDetails(true);
		try {
			const res = await fetch(`/api/ip-details?ip=${ip}`);
			const data = await res.json();
			setIpDetails(data);
		} catch (err) {
			console.error("Ошибка получения информации об IP:", err);
			setIpDetails({ ip, error: "Не удалось загрузить информацию" });
		}
		setIsLoadingIPDetails(false);
	};

	const getSeverityClass = (severity) => {
		switch (severity) {
			case "high":
				return "bg-red-600 text-white";
			case "medium":
				return "bg-yellow-500 text-black";
			case "low":
				return "bg-green-600 text-white";
			default:
				return "bg-gray-500 text-white";
		}
	};

	const translateAttackType = (type) => {
		const types = {
			ddos: 'DDoS',
			bruteforce: 'Brute Force',
			scanner: 'Сканер',
			spam: 'Спам',
			crawler: 'Краулер',
			scraper: 'Скраппер',
			unknown: 'Неизвестно'
		};
		return types[type] || type;
	};

	const translateSeverity = (severity) => {
		const severities = {
			high: 'Высокий',
			medium: 'Средний',
			low: 'Низкий'
		};
		return severities[severity] || severity;
	};

	const handleShowIPDetails = async (ip) => {
		setSelectedIP(ip);
		setShowIPDetailsModal(true);
		await fetchIPDetails(ip);
	};

	const handleShowRequestDetails = (request) => {
		setSelectedRequest(request);
		setShowRequestDetailsModal(true);
	};

	const filteredSuspiciousIPs = useMemo(() => {
		return (domainConfig?.suspicious_ips || []).filter(ip => {
			const matchesThreatType = selectedThreat === 'all' ||
				ip.attack_type?.toLowerCase()?.includes(selectedThreat);
			const matchesSeverity = !showHighSeverityOnly || ip.severity === 'high';
			return matchesThreatType && matchesSeverity;
		});
	}, [domainConfig?.suspicious_ips, selectedThreat, showHighSeverityOnly]);

	const handleIpSearchChange = useCallback((e) => {
		setIpSearch(e.target.value);
	}, []);

	/*useEffect(() => {
		if (filteredSuspiciousIPs.length > 0) {
			setTimedIPs(filteredSuspiciousIPs);
		} else {
			setTimedIPs([]);
		}
	}, [filteredSuspiciousIPs]);*/

	/*useEffect(() => {
		const interval = setInterval(() => {
			setTimedIPs(prevIPs => {
				// Создаем новый массив с обновленным временем
				const updatedIPs = prevIPs.map(ip => ({
					...ip,
					expires_in: ip.expires_in > 0 ? ip.expires_in - 1 : 0
				}));

				// Фильтруем только те, у которых время еще не истекло
				return updatedIPs.filter(ip => ip.expires_in > 0);
			});
		}, 1000);

		return () => clearInterval(interval);
	}, []);*/

	if (!selectedDomain) {
		return <NoDomainsMessage onAddDomain={onAddDomain} />;
	}

	if (isLoading || !domainConfig || requestsLoading) {
		return <div className="flex justify-center py-8">Загрузка конфигурации...</div>;
	}

	return (
		<div className="space-y-8">
			<FeatureBlock feature="blocked_requests_view" isFeatureAvailable={isFeatureAvailable}>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
					<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
						<h3 className="text-lg font-semibold">Заблокированные запросы</h3>
						<div className="flex flex-col xs:flex-row gap-2 md:gap-4">
							<div className="flex gap-2">
								<StableInput
									type="text"
									placeholder="Поиск по IP..."
									value={ipSearch}
									onChange={handleIpSearchChange}
									className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
								/>
								<DarkSelect
									size="sm"
									value={blockedRequestsReason}
									onChange={(value) => setBlockedRequestsReason(value)}
									disabled={!domainConfig?.rate}
									options={[
										{ value: "all", label: "Все причины" },
										{ value: "bot", label: "Bot" },
										{ value: "rate_limit", label: "Rate Limit" },
										{ value: "rules", label: "Rules" },
									]}
								/>
								<DarkSelect
									size="sm"
									value={blockedRequestsLimit}
									onChange={(value) => setBlockedRequestsLimit(Number(value))}
									disabled={!domainConfig?.rate}
									options={[
										{ value: 10, label: "10" },
										{ value: 20, label: "20" },
										{ value: 50, label: "50" },
										{ value: 100, label: "100" },
									]}
								/>

							</div>
							<button
								onClick={refreshBlockedRequests}
								disabled={isRefreshingBlocked || !domainConfig?.rate}
								className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all self-start disabled:opacity-50 disabled:cursor-not-allowed"
								title="Обновить список"
							>
								<FiRefreshCw className={`${isRefreshingBlocked ? 'animate-spin' : ''}`} />
							</button>
						</div>
					</div>

					{requestsLoading ? (
						<div className="flex justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
						</div>
					) : (blockedRequests?.length ?? 0) > 0 ? (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-white/10">
								<thead>
									<tr>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP адрес</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Метод</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Причина</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Запросы</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Время</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Действия</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-white/5">
									{blockedRequests.map((request, idx) => (
										<tr key={idx} className="hover:bg-white/5">
											<td className="px-4 py-3 whitespace-nowrap">
												<button
													onClick={() => handleShowIPDetails(request.ip)}
													className="font-mono text-blue-400 hover:text-blue-300 underline disabled:opacity-50 disabled:cursor-not-allowed"
													title="Подробная информация"
													disabled={!domainConfig?.rate}
												>
													{request.ip}
												</button>
											</td>
											<td className="px-4 py-3 whitespace-nowrap">{request.method}</td>
											<td className="px-4 py-3 whitespace-nowrap">{request.reason}</td>
											<td className="px-4 py-3 whitespace-nowrap">{request.count}</td>
											<td className="px-4 py-3 whitespace-nowrap">{request.last_seen}</td>
											<td className="px-4 py-3 whitespace-nowrap">
												<div className="flex gap-2">
													<button
														onClick={() => handleAddIP(request.ip, 'permanent')}
														className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
														title="Добавить в черный список"
														disabled={!isIPManagementAvailable()}
													>
														<FiLock size={14} />
													</button>
													<button
														onClick={() => handleAddIP(request.ip, 'white_list')}
														className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
														title="Добавить в белый список"
														disabled={!isIPManagementAvailable()}
													>
														<FiCheckCircle size={14} />
													</button>
													<button
														onClick={() => handleShowRequestDetails(request)}
														className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
														title="Подробная информация"
														disabled={!domainConfig?.rate}
													>
														<FiInfo size={14} />
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="text-center py-8 text-gray-400">
							{domainConfig?.rate ? "Нет заблокированных запросов" : "Функция недоступна без тарифа"}
						</div>
					)}
				</div>
			</FeatureBlock>
			<FeatureBlock feature="suspicious_ips" isFeatureAvailable={isFeatureAvailable}>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
					<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
						<h3 className="text-lg font-semibold">Подозрительные IP</h3>
						<div className="flex flex-col xs:flex-row gap-2 md:gap-4">
							<DarkSelect
								size="sm"
								value={selectedThreat}
								onChange={(value) => setSelectedThreat(value)}
								options={[
									{ value: "all", label: "Все типы" },
									{ value: "ddos", label: "DDoS" },
									{ value: "bruteforce", label: "Brute Force" },
									{ value: "scanner", label: "Сканер" },
									{ value: "scraper", label: "Скраппер" },
									{ value: "crawler", label: "Краулер" },
									{ value: "spam", label: "Спам" },
								]}
							/>
							<label className="flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={showHighSeverityOnly}
									onChange={(e) => setShowHighSeverityOnly(e.target.checked)}
									className="rounded bg-white/10 border-white/20"
								/>
								Только высокий риск
							</label>
						</div>
					</div>

					{isLoading ? (
						<div className="flex justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
						</div>
					) : filteredSuspiciousIPs.length > 0 ? (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-white/10">
								<thead>
									<tr>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP адрес</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тип атаки</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Уровень</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Истекает через</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Действия</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-white/5">
									{filteredSuspiciousIPs.map((sip, idx) => (
										<tr key={sip.ip} className="hover:bg-white/5">
											<td className="px-4 py-3 whitespace-nowrap">
												<button
													onClick={() => handleShowIPDetails(sip.ip)}
													className="font-mono text-blue-400 hover:text-blue-300 underline"
													title="Подробная информация"
												>
													{sip.ip}
												</button>
											</td>
											<td className="px-4 py-3 whitespace-nowrap">{translateAttackType(sip.attack_type)}</td>
											<td className="px-4 py-3 whitespace-nowrap">
												<span className={`px-2 py-1 rounded-full text-xs ${getSeverityClass(sip.severity)}`}>
													{translateSeverity(sip.severity)}
												</span>
											</td>
											<td className="px-4 py-3 whitespace-nowrap">
												<Timer key={sip.ip}
													initialExpiresIn={sip.expires_in}
													onExpire={() => {
														// Когда время истекает, можно обновить данные
														// или оставить как есть - строка останется, но покажет "Истекло"
													}}
												/>
											</td>
											<td className="px-4 py-3 whitespace-nowrap">
												<div className="flex gap-2">
													<button
														onClick={() => {
															const updatedConfig = { ...domainConfig };
															delete updatedConfig.blocked_ips.temporary[sip.ip];
															updatedConfig.suspicious_ips = (updatedConfig.suspicious_ips || []).filter(
																(item) => item.ip !== sip.ip
															);
															onUpdateConfig(updatedConfig);
														}}
														className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded-lg text-sm transition-all"
														title="Разблокировать"
													>
														<FiUnlock size={14} />
													</button>
													<button
														onClick={() => handleAddIP(sip.ip, 'permanent')}
														className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-lg text-sm transition-all"
														title="Добавить в черный список"
													>
														<FiLock size={14} />
													</button>
													<button
														onClick={() => handleAddIP(sip.ip, 'white_list')}
														className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-all"
														title="Добавить в белый список"
													>
														<FiCheckCircle size={14} />
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="text-center py-8 text-gray-400">Нет подозрительных ip</div>
					)}
				</div>
			</FeatureBlock>

			{/* Модальное окно с детальной информацией о запросе */}
			<AnimatePresence>
				{showRequestDetailsModal && selectedRequest && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
						onClick={() => setShowRequestDetailsModal(false)}
					>
						<motion.div
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							className="bg-gray-800 rounded-2xl p-6 w-full max-w-2xl"
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-xl font-semibold">Детали запроса</h3>
								<button
									onClick={() => setShowRequestDetailsModal(false)}
									className="p-1 hover:bg-white/10 rounded-lg"
								>
									<FiX size={24} />
								</button>
							</div>

							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="bg-white/5 p-4 rounded-lg">
										<h4 className="font-medium mb-2">Основная информация</h4>
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-gray-400">IP адрес:</span>
												<span className="font-mono">{selectedRequest.ip}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-400">Метод:</span>
												<span>{selectedRequest.method}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-400">Статус:</span>
												<span>{selectedRequest.status}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-400">Причина:</span>
												<span>{selectedRequest.reason}</span>
											</div>
										</div>
									</div>

									<div className="bg-white/5 p-4 rounded-lg">
										<h4 className="font-medium mb-2">Дополнительная информация</h4>
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-gray-400">Количество запросов:</span>
												<span>{selectedRequest.count}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-400">Время:</span>
												<span>{selectedRequest.last_seen}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-400">User-Agent:</span>
												<span className="text-sm">{selectedRequest.user_agent}</span>
											</div>
										</div>
									</div>
								</div>

								<div className="bg-white/5 p-4 rounded-lg">
									<h4 className="font-medium mb-2">URL</h4>
									<div className="break-all font-mono text-sm">
										{selectedRequest.url}
									</div>
								</div>

								<div className="flex gap-3 pt-4">
									<button
										onClick={() => {
											handleAddIP(selectedRequest.ip, 'permanent');
											setShowRequestDetailsModal(false);
										}}
										disabled={!isIPManagementAvailable()}
										className={`flex-1 px-4 py-2 bg-red-600 rounded-lg transition-all flex items-center justify-center gap-2 ${isIPManagementAvailable()
											? 'hover:bg-red-700'
											: 'opacity-50 cursor-not-allowed'
											}`}
									>
										<FiLock size={16} />
										Добавить в черный список
									</button>
									<button
										onClick={() => {
											handleAddIP(selectedRequest.ip, 'white_list');
											setShowRequestDetailsModal(false);
										}}
										disabled={!isIPManagementAvailable()}
										className={`flex-1 px-4 py-2 bg-blue-600 rounded-lg transition-all flex items-center justify-center gap-2 ${isIPManagementAvailable()
											? 'hover:bg-blue-700'
											: 'opacity-50 cursor-not-allowed'
											}`}
									>
										<FiCheckCircle size={16} />
										Добавить в белый список
									</button>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
			{/* Модальное окно с детальной информацией об IP */}
			<AnimatePresence>
				{showIPDetailsModal && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
						onClick={() => setShowIPDetailsModal(false)}
					>
						<motion.div
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							className="bg-gray-800 rounded-2xl p-6 w-full max-w-2xl"
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-xl font-semibold">Информация об IP адресе</h3>
								<button
									onClick={() => setShowIPDetailsModal(false)}
									className="p-1 hover:bg-white/10 rounded-lg"
								>
									<FiX size={24} />
								</button>
							</div>

							{isLoadingIPDetails ? (
								<div className="flex justify-center py-8">
									<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
								</div>
							) : ipDetails ? (
								<div className="space-y-4">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="bg-white/5 p-4 rounded-lg">
											<h4 className="font-medium mb-2">Основная информация</h4>
											<div className="space-y-2">
												<div className="flex justify-between">
													<span className="text-gray-400">IP адрес:</span>
													<span className="font-mono">{ipDetails.ip}</span>
												</div>
												<div className="flex justify-between">
													<span className="text-gray-400">Страна:</span>
													<span>{ipDetails.country || 'Неизвестно'}</span>
												</div>
												<div className="flex justify-between">
													<span className="text-gray-400">Город:</span>
													<span>{ipDetails.city || 'Неизвестно'}</span>
												</div>
												<div className="flex justify-between">
													<span className="text-gray-400">Провайдер:</span>
													<span>{ipDetails.isp || 'Неизвестно'}</span>
												</div>
											</div>
										</div>

										<div className="bg-white/5 p-4 rounded-lg">
											<h4 className="font-medium mb-2">Сетевые данные</h4>
											<div className="space-y-2">
												<div className="flex justify-between">
													<span className="text-gray-400">Организация:</span>
													<span>{ipDetails.org || 'Неизвестно'}</span>
												</div>
												<div className="flex justify-between">
													<span className="text-gray-400">ASN:</span>
													<span>{ipDetails.asn || 'Неизвестно'}</span>
												</div>
												{ipDetails.reverseDNS && (
													<div className="flex justify-between">
														<span className="text-gray-400">Обратный DNS:</span>
														<span className="font-mono text-sm">{ipDetails.reverseDNS}</span>
													</div>
												)}
											</div>
										</div>
									</div>

									<div className="flex gap-3 pt-4">
										<button
											onClick={() => {
												handleAddIP(selectedIP, 'permanent');
												setShowIPDetailsModal(false);
											}}
											disabled={!isIPManagementAvailable()}
											className={`flex-1 px-4 py-2 bg-red-600 rounded-lg transition-all flex items-center justify-center gap-2 ${isIPManagementAvailable()
												? 'hover:bg-red-700'
												: 'opacity-50 cursor-not-allowed'
												}`}
										>
											<FiLock size={16} />
											Добавить в черный список
										</button>
										<button
											onClick={() => {
												handleAddIP(selectedIP, 'white_list');
												setShowIPDetailsModal(false);
											}}
											disabled={!isIPManagementAvailable()}
											className={`flex-1 px-4 py-2 bg-blue-600 rounded-lg transition-all flex items-center justify-center gap-2 ${isIPManagementAvailable()
												? 'hover:bg-blue-700'
												: 'opacity-50 cursor-not-allowed'
												}`}
										>
											<FiCheckCircle size={16} />
											Добавить в белый список
										</button>
									</div>
								</div>
							) : (
								<div className="text-center py-8 text-gray-400">
									Не удалось загрузить информацию об IP адресе
								</div>
							)}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
});

function LogsTab({ selectedDomain, selectedDomainName, onAddDomain }) {
	const [isLoading, setIsLoading] = useState(false);
	const [isEstimating, setIsEstimating] = useState(false);
	const [error, setError] = useState(null);

	// Функция для преобразования Date в формат datetime-local
	const toLocalInput = (date) => {
		if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
			date = new Date();
		}

		const pad = (n) => n.toString().padStart(2, '0');
		const year = date.getFullYear();
		const month = pad(date.getMonth() + 1);
		const day = pad(date.getDate());
		const hours = pad(date.getHours());
		const minutes = pad(date.getMinutes());

		return `${year}-${month}-${day}T${hours}:${minutes}`;
	};

	const now = new Date();
	const start = new Date(now.getTime() - 3600000); // 1 час назад

	const [exportSettings, setExportSettings] = useState({
		timeRange: '1h',
		startTime: toLocalInput(start),
		endTime: toLocalInput(now),
		logType: 'all',
		ipFilter: '',
		userAgentFilter: '',
		statusCodeFilter: '',
		pathFilter: '',
		format: 'csv',
		limit: 10000,
	});

	const [estimatedCount, setEstimatedCount] = useState(null);

	const handleTimeRangeChange = (range) => {
		const now = new Date();

		if (range === 'custom') {
			// Для "Вручную" просто устанавливаем режим без изменения времени
			setExportSettings(prev => ({
				...prev,
				timeRange: 'custom'
			}));
			return;
		}

		let start = new Date();

		switch (range) {
			case '1h':
				start.setHours(start.getHours() - 1);
				break;
			case '6h':
				start.setHours(start.getHours() - 6);
				break;
			case '12h':
				start.setHours(start.getHours() - 12);
				break;
			case '24h':
				start.setDate(start.getDate() - 1);
				break;
			default:
				start.setHours(start.getHours() - 1); // По умолчанию 1 час
		}

		setExportSettings(prev => ({
			...prev,
			timeRange: range,
			startTime: toLocalInput(start),
			endTime: toLocalInput(now)
		}));
	};

	const validateSettings = () => {
		setError(null);

		const startDate = new Date(exportSettings.startTime);
		const endDate = new Date(exportSettings.endTime);

		// Проверяем валидность дат
		if (isNaN(startDate.getTime())) {
			setError('Неверный формат начальной даты');
			return false;
		}

		if (isNaN(endDate.getTime())) {
			setError('Неверный формат конечной даты');
			return false;
		}

		// Проверяем, что начальная дата раньше конечной
		if (startDate >= endDate) {
			setError('Начальная дата должна быть раньше конечной');
			return false;
		}

		// Проверяем, что интервал не превышает 24 часа
		const diffMs = endDate.getTime() - startDate.getTime();
		const diffHours = diffMs / (1000 * 60 * 60);

		if (diffHours > 24) {
			setError('Максимальный интервал - 24 часа');
			return false;
		}

		// Проверяем, что дата не в будущем
		if (endDate > new Date()) {
			setError('Конечная дата не может быть в будущем');
			return false;
		}

		return true;
	};

	const estimateExportSize = async () => {
		if (!validateSettings()) {
			setEstimatedCount(0);
			return;
		}

		try {
			const response = await fetch('/api/logs/estimate', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: "include",
				body: JSON.stringify({
					domain: selectedDomain,
					startTime: new Date(exportSettings.startTime).toISOString(),
					endTime: new Date(exportSettings.endTime).toISOString(),
					logType: exportSettings.logType,
					ipFilter: exportSettings.ipFilter,
					userAgentFilter: exportSettings.userAgentFilter,
					statusCodeFilter: exportSettings.statusCodeFilter,
					pathFilter: exportSettings.pathFilter,
					format: exportSettings.format,
					limit: exportSettings.limit,
				})
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Ошибка оценки размера');
			}

			const data = await response.json();
			setEstimatedCount(data.estimatedCount || 0);
		} catch (error) {
			console.error("Ошибка оценки размера:", error);
			setEstimatedCount(0);
			setError(error.message);
		}
	};

	const handleExport = async () => {
		if (!validateSettings()) {
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const response = await fetch('/api/logs/export', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: "include",
				body: JSON.stringify({
					domain: selectedDomain,
					startTime: new Date(exportSettings.startTime).toISOString(),
					endTime: new Date(exportSettings.endTime).toISOString(),
					logType: exportSettings.logType,
					ipFilter: exportSettings.ipFilter,
					userAgentFilter: exportSettings.userAgentFilter,
					statusCodeFilter: exportSettings.statusCodeFilter,
					pathFilter: exportSettings.pathFilter,
					format: exportSettings.format,
					limit: exportSettings.limit,
				})
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || `Ошибка экспорта: ${response.statusText}`);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `logs_${selectedDomainName || selectedDomain}_${Date.now()}.${exportSettings.format}`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);

		} catch (error) {
			console.error("Ошибка экспорта:", error);
			setError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	// Обновляем оценку при изменении настроек
	useEffect(() => {
		const timer = setTimeout(() => {
			setIsEstimating(true);
			estimateExportSize().finally(() => setIsEstimating(false));
		}, 500);
		return () => clearTimeout(timer);
	}, [exportSettings]);

	if (!selectedDomain) {
		return <NoDomainsMessage onAddDomain={onAddDomain} />;
	}

	// Функция для форматирования времени
	const formatTime = (dateString) => {
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return 'Неверная дата';
			return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		} catch {
			return 'Неверная дата';
		}
	};

	// Функция для форматирования диапазона дат
	const formatDateRange = () => {
		try {
			const startDate = new Date(exportSettings.startTime);
			const endDate = new Date(exportSettings.endTime);

			if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
				return 'Неверный формат даты';
			}

			const isSameDay = startDate.toDateString() === endDate.toDateString();

			if (isSameDay) {
				return `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
			} else {
				return `${startDate.toLocaleString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleString([], { hour: '2-digit', minute: '2-digit' })}`;
			}
		} catch {
			return 'Неверный формат даты';
		}
	};

	return (
		<div className="space-y-6">
			{error && (
				<div className="bg-red-900/20 border border-red-700 text-red-400 p-4 rounded-lg">
					<div className="flex items-center gap-2">
						<FiAlertTriangle />
						<span>{error}</span>
					</div>
				</div>
			)}

			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
					<FiFileText className="text-blue-400" />
					Экспорт логов запросов
				</h2>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Левая колонка - настройки фильтров */}
					<div className="lg:col-span-2 space-y-6">
						{/* Быстрый выбор временного интервала */}
						<div>
							<label className="block text-sm font-medium text-gray-400 mb-3">Временной интервал</label>
							<div className="flex flex-wrap gap-2 mb-4">
								{['1h', '6h', '12h', '24h', 'custom'].map(range => (
									<button
										key={range}
										onClick={() => handleTimeRangeChange(range)}
										className={`px-4 py-2 rounded-lg transition-all ${exportSettings.timeRange === range ? 'bg-blue-600 text-white' : 'bg-white/5 hover:bg-white/10'}`}
									>
										{range === '1h' ? '1 час' :
											range === '6h' ? '6 часов' :
												range === '12h' ? '12 часов' :
													range === '24h' ? '24 часа' : 'Вручную'}
									</button>
								))}
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Начало</label>
									<input
										type="datetime-local"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.startTime}
										onChange={e => setExportSettings(prev => ({
											...prev,
											startTime: e.target.value,
											timeRange: 'custom' // При ручном изменении переключаем на кастом
										}))}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Конец</label>
									<input
										type="datetime-local"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.endTime}
										onChange={e => setExportSettings(prev => ({
											...prev,
											endTime: e.target.value,
											timeRange: 'custom' // При ручном изменении переключаем на кастом
										}))}
										max={toLocalInput(new Date())} // Нельзя выбрать будущее время
									/>
								</div>
							</div>

							<p className="text-sm text-gray-400 mt-2">
								Максимальный интервал: 24 часа
							</p>
						</div>

						{/* Фильтры */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold flex items-center gap-2">
								<FiFilter />
								Фильтры
							</h3>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Тип логов</label>
									<DarkSelect
										value={exportSettings.logType}
										onChange={(value) =>
											setExportSettings(prev => ({ ...prev, logType: value }))
										}

										options={[
											{ value: "all", label: "Все запросы" },
											{ value: "blocked", label: "Только заблокированные" },
											{ value: "allowed", label: "Только пропущенные" },
										]}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Формат экспорта</label>
									<DarkSelect
										value={exportSettings.format}
										onChange={(value) =>
											setExportSettings(prev => ({ ...prev, format: value }))
										}
										options={[
											{ value: "csv", label: "CSV (Excel)" },

											{ value: "json", label: "JSON" },
										]}
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">IP адрес (опционально)</label>
									<input
										type="text"
										placeholder="192.168.1.1"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.ipFilter}
										onChange={e => setExportSettings(prev => ({ ...prev, ipFilter: e.target.value }))}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">User Agent (опционально)</label>
									<input
										type="text"
										placeholder="Mozilla/5.0..."
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.userAgentFilter}
										onChange={e => setExportSettings(prev => ({ ...prev, userAgentFilter: e.target.value }))}
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Код ответа (опционально)</label>
									<input
										type="text"
										placeholder="200"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.statusCodeFilter}
										onChange={e => setExportSettings(prev => ({ ...prev, statusCodeFilter: e.target.value }))}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-400 mb-1">Путь (опционально)</label>
									<input
										type="text"
										placeholder="/api/"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
										value={exportSettings.pathFilter}
										onChange={e => setExportSettings(prev => ({ ...prev, pathFilter: e.target.value }))}
									/>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-400 mb-1">
									Максимальное количество записей: {exportSettings.limit.toLocaleString()}
								</label>
								<input
									type="range"
									min="100"
									max="50000"
									step="100"
									className="w-full"
									value={exportSettings.limit}
									onChange={e => setExportSettings(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
								/>
								<div className="flex justify-between text-sm text-gray-400 mt-1">
									<span>100</span>
									<span>10,000</span>
									<span>50,000</span>
								</div>
							</div>
						</div>
					</div>

					{/* Правая колонка - предпросмотр и кнопка экспорта */}
					<div className="space-y-6">
						<div className="bg-white/5 p-4 rounded-lg">
							<h3 className="font-semibold mb-3">Предпросмотр экспорта</h3>

							{isEstimating ? (
								<div className="flex items-center justify-center py-4">
									<FiRefreshCw className="animate-spin mr-2" />
									<span>Оценка количества записей...</span>
								</div>
							) : estimatedCount !== null ? (
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="text-gray-400">Примерное количество записей:</span>
										<span className="font-bold">{estimatedCount.toLocaleString()}</span>
									</div>

									<div className="flex justify-between">
										<span className="text-gray-400">Временной интервал:</span>
										<span className="text-sm">{formatDateRange()}</span>
									</div>

									<div className="flex justify-between">
										<span className="text-gray-400">Тип логов:</span>
										<span>
											{exportSettings.logType === 'all' ? 'Все' :
												exportSettings.logType === 'blocked' ? 'Только заблокированные' : 'Только пропущенные'}
										</span>
									</div>

									{exportSettings.ipFilter && (
										<div className="flex justify-between">
											<span className="text-gray-400">Фильтр по IP:</span>
											<span className="font-mono text-sm">{exportSettings.ipFilter}</span>
										</div>
									)}

									<div className="pt-3 border-t border-white/10">
										<button
											onClick={handleExport}
											disabled={isLoading || estimatedCount === 0}
											className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-all flex items-center justify-center gap-2 font-semibold"
										>
											{isLoading ? (
												<>
													<FiRefreshCw className="animate-spin" />
													Подготовка выгрузки...
												</>
											) : (
												<>
													<FiDownload />
													Выгрузить логи ({exportSettings.format.toUpperCase()})
												</>
											)}
										</button>

										{estimatedCount > 10000 && (
											<p className="text-yellow-400 text-sm mt-2">
												⚠️ Большое количество записей. Экспорт может занять некоторое время.
											</p>
										)}

										{estimatedCount === 0 && !error && (
											<p className="text-gray-400 text-sm mt-2">
												Нет записей, соответствующих фильтрам
											</p>
										)}
									</div>
								</div>
							) : (
								<div className="text-gray-400 py-4">
									Укажите параметры для оценки количества записей
								</div>
							)}
						</div>

						{/* Предварительный просмотр формата */}
						<div className="bg-white/5 p-4 rounded-lg">
							<h3 className="font-semibold mb-3">Пример данных ({exportSettings.format.toUpperCase()})</h3>
							<pre className="text-xs bg-black/30 p-3 rounded-lg overflow-x-auto">
								{exportSettings.format === 'csv' ?
									`timestamp;ip;method;path;status;user_agent;blocked;reason
"2024-01-15 14:30:22";"192.168.1.1";"GET";"/api/data";200;"Mozilla/5.0...";false;""
"2024-01-15 14:30:25";"10.0.0.1";"POST";"/login";403;"curl/7.68.0";true;"rate_limit"` :
									`[
  {
    "timestamp": "2024-01-15T14:30:22Z",
    "ip": "192.168.1.1",
    "method": "GET",
    "path": "/api/data",
    "status": 200,
    "user_agent": "Mozilla/5.0...",
    "blocked": false,
    "reason": null
  }
]`}
							</pre>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function ArchiveTab({
	selectedDomain,
	selectedDomainName,
	onAddDomain,
	archiveMetrics,
	archiveTraffic,
	archiveGeo,
	archiveLoading,
	archivePeriod,
	setArchivePeriod,
	archiveRange,
	setArchiveRange,
	onLoadArchive,
	userRate,
	aiData,
	aiLoading,
	onLoadAI,
}) {
	const hasFullAccess = userRate === 'Test' || userRate.startsWith("Full");

	if (!selectedDomain) {
		return <NoDomainsMessage onAddDomain={onAddDomain} />;
	}

	const handleLoadAll = async () => {
		await onLoadArchive();
		await onLoadAI();
	};

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-semibold">
					Архив статистики для <span className="text-blue-400">{selectedDomainName}</span>
				</h2>
				<button
					onClick={handleLoadAll}
					disabled={archiveLoading || aiLoading}
					className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-all"
				>
					<FiRefreshCw className={(archiveLoading || aiLoading) ? 'animate-spin' : ''} />
					Загрузить
				</button>
			</div>

			{/* Фильтры */}
			<div className="relative z-20 flex flex-wrap items-end gap-4 bg-white/5 backdrop-blur-lg rounded-2xl p-4 border border-white/10">
				<div className="flex flex-col gap-1">
					<label className="text-sm text-gray-400">Период агрегации</label>
					<DarkSelect
						value={archivePeriod}
						onChange={(value) => setArchivePeriod(value)}
						options={[
							{ value: 'hourly', label: 'По часам' },
							{ value: 'daily', label: 'По дням' },
							{ value: 'monthly', label: 'По месяцам' },
						]}
						className="w-40"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className="text-sm text-gray-400">С даты</label>
					<input
						type="date"
						value={archiveRange.from}
						onChange={(e) => setArchiveRange({ ...archiveRange, from: e.target.value })}
						className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className="text-sm text-gray-400">По дату</label>
					<input
						type="date"
						value={archiveRange.to}
						onChange={(e) => setArchiveRange({ ...archiveRange, to: e.target.value })}
						className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
			</div>

			{/* Сводка */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<StatCard
					title="Всего запросов"
					value={archiveMetrics?.totalRequests ?? 0}
					change={undefined}
					icon={<FiActivity className="text-blue-400" />}
					loading={archiveLoading}
				/>
				<StatCard
					title="Блокировано"
					value={archiveMetrics?.blockedRequests ?? 0}
					change={undefined}
					icon={<FiShield className="text-purple-400" />}
					loading={archiveLoading}
				/>
				<StatCard
					title="Время ответа"
					value={
						archiveMetrics?.responseTime != null
							? `${archiveMetrics.responseTime} мс`
							: '—'
					}
					change={undefined}
					icon={<FiClock className="text-green-400" />}
					loading={archiveLoading}
				/>
			</div>

			{/* Графики */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">Трафик</h3>
					<div className="h-[calc(100%-2rem)]">
						{archiveTraffic.length > 0 ? (
							<TrafficChart data={archiveTraffic} />
						) : (
							<div className="flex items-center justify-center h-full text-gray-400">
								{archiveLoading ? 'Загрузка...' : 'Нет данных'}
							</div>
						)}
					</div>
				</div>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">География атак</h3>
					<div className="h-[calc(100%-2rem)]">
						{archiveGeo.length > 0 ? (
							<GeoChart data={archiveGeo} />
						) : (
							<div className="flex items-center justify-center h-full text-gray-400">
								{archiveLoading ? 'Загрузка...' : 'Нет данных'}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* AI Защита */}
			<div className="mt-10">
				<h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
					<FiZap className="text-yellow-400" />
					AI Защита
				</h2>

				{hasFullAccess ? (
					<AIFullAnalytics data={aiData} loading={aiLoading} />
				) : (
					<AISummary data={aiData} loading={aiLoading} />
				)}
			</div>
		</div>
	);
}

const AISummary = memo(({ data, loading }) => {
	if (loading) {
		return (
			<div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center text-gray-400">
				Загрузка данных AI‑защиты...
			</div>
		);
	}

	if (!data || data.totalIPs == null) {
		return (
			<div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center text-gray-400">
				Нет данных о работе AI‑защиты за выбранный период.
			</div>
		);
	}

	const { totalIPs, attackTypes } = data;

	return (
		<motion.div
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 space-y-6"
		>
			<div className="flex items-start justify-between">
				<div className="space-y-4">
					<div className="flex items-center gap-3">
						<FiZap className="text-3xl text-yellow-400" />
						<div>
							<p className="text-gray-300">Обнаружено подозрительных IP</p>
							<p className="text-3xl font-bold text-white">{totalIPs}</p>
						</div>
					</div>

					{attackTypes && attackTypes.length > 0 && (
						<div>
							<p className="text-gray-400 mb-2">Типы атак:</p>
							<div className="flex flex-wrap gap-3">
								{attackTypes.map((item) => (
									<span
										key={item.type}
										className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-200"
									>
										{item.type} – <strong>{item.count}</strong>
									</span>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="border-t border-white/10 pt-6 space-y-4">
				<p className="text-gray-300 text-sm">
					📊 Полная аналитика по каждому IP, графики атак и автоматические блокировки доступны только в тарифе <span className="text-yellow-400 font-semibold">Full</span>.
				</p>
				<a
					href="/#pricing"
					className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-lg hover:scale-105 transition-transform"
				>
					<FaCrown />
					Перейти на Full
				</a>
			</div>
		</motion.div>
	);
});

const AIFullAnalytics = memo(({ data, loading }) => {
	if (loading) {
		return (
			<div className="text-center text-gray-400 py-12">Загрузка аналитики AI...</div>
		);
	}

	if (!data || data.totalIPs == null) {
		return (
			<div className="text-center text-gray-400 py-12">
				Нет данных AI‑защиты за выбранный период.
			</div>
		);
	}

	const { totalIPs, timeSeries, attackTypes } = data;

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
				<StatCard
					title="Обнаружено IP"
					value={totalIPs}
					change={undefined}
					icon={<FiZap className="text-yellow-400" />}
				/>
				{/* Можно добавить ещё карточки, если понадобятся */}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* График обнаружений по времени */}
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">Обнаружения по времени</h3>
					<div className="h-[calc(100%-2rem)]">
						{timeSeries?.length > 0 ? (
							<ResponsiveContainer width="100%" height="100%">
								<LineChart data={timeSeries}>
									<CartesianGrid strokeDasharray="3 3" stroke="#374151" />
									<XAxis dataKey="time" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
									<YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
									<Tooltip
										contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', color: '#FFF' }}
									/>
									<Line type="monotone" dataKey="count" stroke="#F59E0B" strokeWidth={2} dot={false} />
								</LineChart>
							</ResponsiveContainer>
						) : (
							<div className="flex items-center justify-center h-full text-gray-400">
								Нет данных
							</div>
						)}
					</div>
				</div>

				{/* Круговая диаграмма типов атак */}
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl h-80">
					<h3 className="text-lg font-semibold mb-4">Типы атак</h3>
					<div className="h-[calc(100%-2rem)]">
						{attackTypes?.length > 0 ? (
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Pie
										data={attackTypes}
										dataKey="count"
										nameKey="type"
										cx="50%"
										cy="50%"
										outerRadius={80}
										label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
									>
										{attackTypes.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={['#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#10B981'][index % 5]}
											/>
										))}
									</Pie>
									<Tooltip
										contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', color: '#FFF' }}
									/>
								</PieChart>
							</ResponsiveContainer>
						) : (
							<div className="flex items-center justify-center h-full text-gray-400">
								Нет данных
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
});

const StableInput = memo(({ value, onChange, ...props }) => (
	<input {...props} value={value} onChange={onChange} />
));

// Основной компонент ConfigurationTab
const ConfigurationTab = memo(function ConfigurationTab({
	domainConfig,
	onUpdateConfig,
	isLoading,
	selectedDomain,
	onAddDomain
}) {
	const [newIP, setNewIP] = useState('');
	const [newCountry, setNewCountry] = useState('');
	const [ipListType, setIpListType] = useState('white_list');
	const [editingSensitivity, setEditingSensitivity] = useState({
		proxy: domainConfig?.proxy_sensitivity || 50,
		ai: domainConfig?.ai_sensitivity || 50
	});
	const [newAllowedURL, setNewAllowedURL] = useState('');
	const [newAllowedUA, setNewAllowedUA] = useState('');
	const [addMode, setAddMode] = useState('single');
	const [multipleIPs, setMultipleIPs] = useState('');

	useEffect(() => {
		if (domainConfig) {
			setEditingSensitivity({
				proxy: domainConfig.proxy_sensitivity || 50,
				ai: domainConfig.ai_sensitivity || 50
			});
		}
	}, [domainConfig]);

	const isFeatureAvailable = useCallback((feature) => {
		const rate = domainConfig?.rate || '';
		if (!rate) return false;
		if (rate.startsWith("Test")) return true;
		if (rate.startsWith("Base")) return feature === 'bots';
		if (rate.startsWith("Pro")) return feature !== 'ai_sensitivity';
		if (rate.startsWith("Full")) return true;
		return false;
	}, [domainConfig?.rate]);

	const handleAddIP = useCallback((ip = null, listType = null) => {
		if (!isFeatureAvailable('ip_management')) return;

		const ipToAdd = ip || newIP;
		const targetListType = listType || ipListType;

		if (!ipToAdd || !isValidIP(ipToAdd)) return;

		const updatedConfig = { ...domainConfig };
		if (targetListType === 'white_list') {
			updatedConfig.white_list = [...(updatedConfig.white_list || []), ipToAdd];
		} else {
			updatedConfig.blocked_ips = updatedConfig.blocked_ips || { temporary: {}, permanent: [] };
			updatedConfig.blocked_ips.permanent = [...(updatedConfig.blocked_ips.permanent || []), ipToAdd];
		}

		onUpdateConfig(updatedConfig);
		if (!ip) setNewIP('');
	}, [domainConfig, newIP, ipListType, isFeatureAvailable, onUpdateConfig]);

	const handleAddMultipleIPs = useCallback(() => {
		if (!isFeatureAvailable('ip_management')) return;
		if (!multipleIPs.trim()) return;

		const ips = multipleIPs
			.split(/[\n,;]+/)
			.map(ip => ip.trim())
			.filter(ip => ip.length > 0);

		if (ips.length === 0) return;

		const validIPs = ips.filter(ip => isValidIP(ip));
		const invalidIPs = ips.filter(ip => !isValidIP(ip));

		if (invalidIPs.length > 0) {
			alert(`Найдено ${invalidIPs.length} невалидных IP адресов. Они не будут добавлены:\n${invalidIPs.slice(0, 10).join('\n')}${invalidIPs.length > 10 ? '\n...' : ''}`);
		}

		if (validIPs.length === 0) return;

		const updatedConfig = { ...domainConfig };

		if (ipListType === 'white_list') {
			const currentList = updatedConfig.white_list || [];
			updatedConfig.white_list = [...new Set([...currentList, ...validIPs])]; // Убираем дубликаты
		} else {
			updatedConfig.blocked_ips = updatedConfig.blocked_ips || { temporary: {}, permanent: [] };
			const currentList = updatedConfig.blocked_ips.permanent || [];
			updatedConfig.blocked_ips.permanent = [...new Set([...currentList, ...validIPs])];
		}

		onUpdateConfig(updatedConfig);

		alert(`Успешно добавлено ${validIPs.length} IP адресов в ${ipListType === 'white_list' ? 'белый список' : 'черный список'}`);

		if (validIPs.length > 0) {
			setMultipleIPs('');
			setAddMode('single');
		}
	}, [domainConfig, multipleIPs, ipListType, isFeatureAvailable, onUpdateConfig]);

	const handleRemoveIP = useCallback((ip, listType) => {
		if (!isFeatureAvailable('ip_management')) return;

		const updatedConfig = { ...domainConfig };

		if (listType === 'white_list') {
			updatedConfig.white_list = (updatedConfig.white_list || []).filter(item => item !== ip);
		} else {
			updatedConfig.blocked_ips.permanent = (updatedConfig.blocked_ips?.permanent || []).filter(item => item !== ip);
		}

		onUpdateConfig(updatedConfig);
	}, [domainConfig, isFeatureAvailable, onUpdateConfig]);

	const handleAddCountry = useCallback(() => {
		if (!isFeatureAvailable('countries')) return;

		if (!newCountry || newCountry.length !== 2) return;

		const updatedConfig = { ...domainConfig };
		updatedConfig.blocked_countries = [...(updatedConfig.blocked_countries || []), newCountry.toUpperCase()];

		onUpdateConfig(updatedConfig);
		setNewCountry('');
	}, [domainConfig, newCountry, isFeatureAvailable, onUpdateConfig]);

	const handleRemoveCountry = useCallback((country) => {
		if (!isFeatureAvailable('countries')) return;

		const updatedConfig = { ...domainConfig };
		updatedConfig.blocked_countries = (updatedConfig.blocked_countries || []).filter(item => item !== country);

		onUpdateConfig(updatedConfig);
	}, [domainConfig, isFeatureAvailable, onUpdateConfig]);

	const handleSensitivityChange = useCallback((type, value) => {
		if (type === 'ai' && !isFeatureAvailable('ai_sensitivity')) return;
		if (type === 'proxy' && !isFeatureAvailable('sensitivity')) return;

		setEditingSensitivity(prev => ({ ...prev, [type]: value }));

		clearTimeout(window.sensitivityTimeout);
		window.sensitivityTimeout = setTimeout(() => {
			const updatedConfig = { ...domainConfig };
			updatedConfig[type === 'proxy' ? 'proxy_sensitivity' : 'ai_sensitivity'] = value;
			onUpdateConfig(updatedConfig);
		}, 500);
	}, [domainConfig, isFeatureAvailable, onUpdateConfig]);

	const handleAddURL = useCallback(() => {
		if (!newAllowedURL) return;
		const updatedConfig = { ...domainConfig };
		updatedConfig.bots = updatedConfig.bots || { enabled: true, allowed_urls: [], allowed_ua: [] };
		updatedConfig.bots.allowed_urls = [...(updatedConfig.bots.allowed_urls || []), newAllowedURL];
		onUpdateConfig(updatedConfig);
		setNewAllowedURL('');
	}, [domainConfig, newAllowedURL, onUpdateConfig]);

	const handleAddUA = useCallback(() => {
		if (!newAllowedUA) return;
		const updatedConfig = { ...domainConfig };
		updatedConfig.bots = updatedConfig.bots || { enabled: true, allowed_urls: [], allowed_ua: [] };
		updatedConfig.bots.allowed_ua = [...(updatedConfig.bots.allowed_ua || []), newAllowedUA];
		onUpdateConfig(updatedConfig);
		setNewAllowedUA('');
	}, [domainConfig, newAllowedUA, onUpdateConfig]);

	const handleRemoveURL = useCallback((url) => {
		const updatedConfig = { ...domainConfig };
		updatedConfig.bots.allowed_urls = (updatedConfig.bots.allowed_urls || []).filter(u => u !== url);
		onUpdateConfig(updatedConfig);
	}, [domainConfig, onUpdateConfig]);

	const handleRemoveUA = useCallback((ua) => {
		const updatedConfig = { ...domainConfig };
		updatedConfig.bots.allowed_ua = (updatedConfig.bots.allowed_ua || []).filter(u => u !== ua);
		onUpdateConfig(updatedConfig);
	}, [domainConfig, onUpdateConfig]);

	const handleToggleBots = useCallback((checked) => {
		const updatedConfig = { ...domainConfig };
		updatedConfig.bots = { ...updatedConfig.bots, enabled: checked };
		onUpdateConfig(updatedConfig);
	}, [domainConfig, onUpdateConfig]);

	if (!selectedDomain) {
		return <NoDomainsMessage onAddDomain={onAddDomain} />;
	}

	if (isLoading || !domainConfig) {
		return <div className="flex justify-center py-8">Загрузка конфигурации...</div>;
	}

	return (
		<div className="space-y-8">
			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<h3 className="text-lg font-semibold mb-6">Настройки чувствительности</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<FeatureBlock feature="sensitivity" isFeatureAvailable={isFeatureAvailable}>
						<div>
							<div className="flex justify-between mb-2">
								<h4 className="font-medium">Чувствительность прокси</h4>
								<span className="text-blue-400">{editingSensitivity.proxy}%</span>
							</div>
							<input
								type="range"
								min="0"
								max="100"
								value={editingSensitivity.proxy}
								onChange={(e) => handleSensitivityChange('proxy', parseInt(e.target.value))}
								className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
							/>
							<div className="flex justify-between text-xs text-gray-400 mt-1">
								<span>Минимальная</span>
								<span>Максимальная</span>
							</div>
						</div>
					</FeatureBlock>

					<FeatureBlock feature="ai_sensitivity" isFeatureAvailable={isFeatureAvailable}>
						<div>
							<div className="flex justify-between mb-2">
								<h4 className="font-medium">Чувствительность AI</h4>
								<span className="text-blue-400">{editingSensitivity.ai}%</span>
							</div>
							<input
								type="range"
								min="0"
								max="100"
								value={editingSensitivity.ai}
								onChange={(e) => handleSensitivityChange('ai', parseInt(e.target.value))}
								className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
							/>
							<div className="flex justify-between text-xs text-gray-400 mt-1">
								<span>Минимальная</span>
								<span>Максимальная</span>
							</div>
						</div>
					</FeatureBlock>
				</div>
			</div>

			<FeatureBlock feature="ip_management" isFeatureAvailable={isFeatureAvailable}>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
					<h3 className="text-lg font-semibold mb-6">Управление IP адресами</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="space-y-4">
							<h4 className="font-medium">Добавить IP адреса</h4>

							<div className="flex space-x-2">
								<DarkSelect
									size="sm"
									value={ipListType}
									onChange={(value) => setIpListType(value)}
									options={[
										{ value: "white_list", label: "Белый список" },
										{ value: "permanent", label: "Черный список" },
									]}
								/>

								<DarkSelect
									size="sm"
									value={addMode}
									onChange={(value) => setAddMode(value)}
									options={[
										{ value: "single", label: "Один IP" },
										{ value: "multiple", label: "Несколько IP" },
									]}
								/>

								<button
									className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
									onClick={() => addMode === 'single' ? handleAddIP() : handleAddMultipleIPs()}
								>
									{addMode === 'single' ? 'Добавить' : 'Добавить все'}
								</button>
							</div>

							{addMode === 'single' ? (
								<StableInput
									type="text"
									placeholder="Введите IP адрес"
									className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
									value={newIP}
									onChange={(e) => setNewIP(e.target.value)}
									onKeyPress={(e) => e.key === 'Enter' && handleAddIP()}
								/>
							) : (
								<div className="space-y-2">
									<textarea
										placeholder="Введите IP адреса, каждый с новой строки или через запятую"
										className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-y"
										value={multipleIPs}
										onChange={(e) => setMultipleIPs(e.target.value)}
										rows={5}
									/>
									<div className="flex justify-between">
										<p className="text-xs text-gray-400">
											Пример формата:<br />
											148.101.40.68<br />
											149.102.41.68, 147.100.46.75<br />
											210.103.56.76; 219.10.40.68
										</p>
										<button
											type="button"
											className="text-xs text-blue-400 hover:text-blue-300 self-end"
											onClick={() => setMultipleIPs("148.101.40.68\n149.102.41.68\n147.100.46.75\n210.103.56.76")}
										>
											Пример
										</button>
									</div>
									<div className="flex gap-2 mt-1">
										<button
											className="flex-1 px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
											onClick={() => setMultipleIPs('')}
										>
											Очистить
										</button>
										<button
											className="flex-1 px-3 py-1 text-sm bg-green-600 hover:bg-green-700 rounded-lg transition-all"
											onClick={handleAddMultipleIPs}
										>
											Добавить все IP
										</button>
									</div>
								</div>
							)}
						</div>

						<div className="space-y-4">
							<h4 className="font-medium">Текущие списки</h4>

							<div className="flex space-x-4">
								<div className="flex-1">
									<h5 className="text-sm font-medium text-green-400 mb-2">
										Белый список ({(domainConfig.white_list || []).length})
									</h5>
									<div className="max-h-40 overflow-y-auto space-y-1">
										{(domainConfig.white_list || []).map((ip, idx) => (
											<div key={idx} className="flex justify-between items-center bg-white/5 p-2 rounded">
												<span className="font-mono text-sm">{ip}</span>
												<button
													onClick={() => handleRemoveIP(ip, 'white_list')}
													className="text-red-400 hover:text-red-300 text-sm"
												>
													Удалить
												</button>
											</div>
										))}
									</div>
								</div>

								<div className="flex-1">
									<h5 className="text-sm font-medium text-red-400 mb-2">
										Черный список ({(domainConfig.blocked_ips?.permanent || []).length})
									</h5>
									<div className="max-h-40 overflow-y-auto space-y-1">
										{(domainConfig.blocked_ips?.permanent || []).map((ip, idx) => (
											<div key={idx} className="flex justify-between items-center bg-white/5 p-2 rounded">
												<span className="font-mono text-sm">{ip}</span>
												<button
													onClick={() => handleRemoveIP(ip, 'permanent')}
													className="text-green-400 hover:text-green-300 text-sm"
												>
													Удалить
												</button>
											</div>
										))}
									</div>
								</div>
							</div>

							{/* Кнопки для очистки списков */}
							<div className="flex space-x-2 mt-4">
								<button
									className="flex-1 px-3 py-2 text-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-all"
									onClick={() => {
										if (window.confirm(`Очистить весь белый список? (${(domainConfig.white_list || []).length} IP)`)) {
											const updatedConfig = { ...domainConfig };
											updatedConfig.white_list = [];
											onUpdateConfig(updatedConfig);
										}
									}}
									disabled={(domainConfig.white_list || []).length === 0}
								>
									Очистить белый список
								</button>
								<button
									className="flex-1 px-3 py-2 text-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-all"
									onClick={() => {
										if (window.confirm(`Очистить весь черный список? (${(domainConfig.blocked_ips?.permanent || []).length} IP)`)) {
											const updatedConfig = { ...domainConfig };
											updatedConfig.blocked_ips = updatedConfig.blocked_ips || { temporary: {}, permanent: [] };
											updatedConfig.blocked_ips.permanent = [];
											onUpdateConfig(updatedConfig);
										}
									}}
									disabled={(domainConfig.blocked_ips?.permanent || []).length === 0}
								>
									Очистить черный список
								</button>
							</div>
						</div>
					</div>
				</div>
			</FeatureBlock>

			<FeatureBlock feature="bots" isFeatureAvailable={isFeatureAvailable}>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
					<h3 className="text-lg font-semibold mb-6">Защита от ботов</h3>
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<div>
								<h4 className="font-medium">Защита от ботов</h4>
								<p className="text-sm text-gray-400">Блокировка подозрительных ботов и скриптов</p>
							</div>
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									className="sr-only peer"
									checked={domainConfig.bots?.enabled || false}
									onChange={(e) => handleToggleBots(e.target.checked)}
								/>
								<div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
							</label>
						</div>
						<div>
							<h4 className="font-medium mb-2">Разрешённые URL</h4>
							<div className="flex space-x-2 mb-2">
								<StableInput
									type="text"
									placeholder="/api/"
									className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
									value={newAllowedURL}
									onChange={(e) => setNewAllowedURL(e.target.value)}
								/>
								<button
									className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
									onClick={handleAddURL}
								>
									Добавить
								</button>
							</div>
							<div className="flex flex-wrap gap-2">
								{(domainConfig.bots?.allowed_urls || []).map((url, idx) => (
									<div key={idx} className="flex items-center space-x-2 bg-white/5 px-2 py-1 rounded">
										<span className="text-sm">{url}</span>
										<button
											onClick={() => handleRemoveURL(url)}
											className="text-red-400 hover:text-red-300 text-sm"
										>
											✕
										</button>
									</div>
								))}
							</div>
						</div>
						<div>
							<h4 className="font-medium mb-2">Разрешённые User-Agent</h4>
							<div className="flex space-x-2 mb-2">
								<StableInput
									type="text"
									placeholder="Mozilla/5.0"
									className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
									value={newAllowedUA}
									onChange={(e) => setNewAllowedUA(e.target.value)}
								/>
								<button
									className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
									onClick={handleAddUA}
								>
									Добавить
								</button>
							</div>
							<div className="flex flex-wrap gap-2">
								{(domainConfig.bots?.allowed_ua || []).map((ua, idx) => (
									<div key={idx} className="flex items-center space-x-2 bg-white/5 px-2 py-1 rounded">
										<span className="text-sm">{ua}</span>
										<button
											onClick={() => handleRemoveUA(ua)}
											className="text-red-400 hover:text-red-300 text-sm"
										>
											✕
										</button>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</FeatureBlock>

			<FeatureBlock feature="countries" isFeatureAvailable={isFeatureAvailable}>
				<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
					<h3 className="text-lg font-semibold mb-6">Управление странами</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="space-y-4">
							<h4 className="font-medium">Добавить страну</h4>
							<div className="flex space-x-2">
								<StableInput
									type="text"
									placeholder="Код страны (RU, CN, US)"
									className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
									value={newCountry}
									onChange={(e) => setNewCountry(e.target.value.toUpperCase())}
									maxLength={2}
								/>
								<button
									className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
									onClick={handleAddCountry}
								>
									Добавить
								</button>
							</div>
							<p className="text-sm text-gray-400">
								Введите двухбуквенный код страны для блокировки
							</p>
						</div>
						<div className="space-y-4">
							<h4 className="font-medium">Заблокированные страны ({(domainConfig.blocked_countries || []).length})</h4>
							<div className="grid grid-cols-3 gap-2">
								{(domainConfig.blocked_countries || []).map((country, idx) => (
									<div key={idx} className="flex justify-between items-center bg-white/5 p-2 rounded">
										<span className="text-sm">{country}</span>
										<button
											onClick={() => handleRemoveCountry(country)}
											className="text-red-400 hover:text-red-300 text-sm"
										>
											Удалить
										</button>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</FeatureBlock>
		</div>
	);
});

function AccountTab({ domains, onAddDomain, onVerifyDomain, onDeleteDomain, onRefreshDomains }) {
	const [accountInfo, setAccountInfo] = useState({
		email: '',
		createdAt: '',
		telegram_connected: false
	});
	const [isLoadingAccount, setIsLoadingAccount] = useState(true);
	const [showVerificationModal, setShowVerificationModal] = useState(false);
	const [showProxySetupModal, setShowProxySetupModal] = useState(false);
	const [verificationData, setVerificationData] = useState({
		domain: '',
		domainId: null,
		txtRecord: ''
	});
	const [proxySetupData, setProxySetupData] = useState({
		domain: '',
		domainId: null,
		nodes: [],             // вместо balancerIP
		acmeCNAME: ''          // вместо balancerCNAME
	});
	const [domainToDelete, setDomainToDelete] = useState(null);
	const [isLoadingVerification, setIsLoadingVerification] = useState(false);
	const [isLoadingProxySettings, setIsLoadingProxySettings] = useState(false);
	const [notifications, setNotifications] = useState({
		push: false,
		telegram: false,
		email: true
	});
	const [showCertModal, setShowCertModal] = useState(false);
	const [certManagement, setCertManagement] = useState({
		domainId: null,
		domainName: '',
		cert: '',
		key: '',
		type: 'custom',
		mode: 'view' // 'view', 'edit', 'upload'
	});
	const [isLoadingCerts, setIsLoadingCerts] = useState(false);
	const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [subscriptionInfo, setSubscriptionInfo] = useState({
		plan: "Нет подписки",
		status: "inactive",
		expiresAt: null,
		autoRenew: false,
		plans: []
	});
	const [showPlanModal, setShowPlanModal] = useState(false);
	const [showRenewModal, setShowRenewModal] = useState(false);
	const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
	const [showPushModal, setShowPushModal] = useState(false);

	const [downloadingArchive, setDownloadingArchive] = useState(false);
	const [archiveDomainId, setArchiveDomainId] = useState(null);
	const [domainSearch, setDomainSearch] = useState('');

	useEffect(() => {
		async function fetchAccountInfo() {
			setIsLoadingAccount(true);
			try {
				const res = await fetch("/api/ui/account/info", { credentials: "include" });
				if (res.ok) {
					const data = await res.json();
					if (data.type === "success") {
						setAccountInfo({
							email: data.data.email,
							createdAt: data.data.created_at,
							telegram_connected: data.data.telegram_connected
						});
					}
				}
			} catch (err) {
				console.error("Ошибка загрузки данных аккаунта:", err);
			} finally {
				setIsLoadingAccount(false);
			}
		}
		fetchAccountInfo();
	}, []);

	useEffect(() => {
		async function fetchNotifications() {
			try {
				const res = await fetch("/api/ui/notifications/get", {
					credentials: "include"
				});

				if (res.ok) {
					const data = await res.json();
					if (data.type === "success") {
						setNotifications(data.data);
					}
				}
			} catch (error) {
				console.error('Ошибка загрузки настроек уведомлений:', error);
			} finally {
				setIsLoadingNotifications(false);
			}
		}
		fetchNotifications();
	}, []);

	// Сохранение настроек уведомлений
	const saveNotifications = async () => {
		try {
			const res = await fetch("/api/ui/notifications/update", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(notifications),
			});

			if (res.ok) {
				alert('Настройки уведомлений сохранены!');
			}
		} catch (error) {
			console.error('Ошибка сохранения уведомлений:', error);
			alert('Ошибка сохранения уведомлений');
		}
	};

	// Сброс настроек
	const resetNotifications = async () => {
		try {
			const res = await fetch("/api/ui/notifications/get", {
				credentials: "include"
			});

			if (res.ok) {
				const data = await res.json();
				if (data.type === "success") {
					setNotifications(data.data);
				}
			}
		} catch (error) {
			console.error('Ошибка сброса настроек:', error);
		}
	};

	// Обработчик изменений
	const handleNotificationChange = (type) => {
		setNotifications(prev => ({
			...prev,
			[type]: !prev[type]
		}));
	};

	function isPushSupported() {
		return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
	}

	// Функция для преобразования ключа
	function urlBase64ToUint8Array(base64String) {
		const padding = '='.repeat((4 - base64String.length % 4) % 4);
		const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
		const rawData = atob(base64);
		const outputArray = new Uint8Array(rawData.length);

		for (let i = 0; i < rawData.length; ++i) {
			outputArray[i] = rawData.charCodeAt(i);
		}
		return outputArray;
	}

	// Функция для получения VAPID ключа
	async function getVAPIDPublicKey() {
		try {
			const res = await fetch('/api/vapid-public-key');
			const data = await res.json();
			return data.publicKey;
		} catch (error) {
			console.error('Ошибка получения VAPID ключа:', error);
			throw error;
		}
	}

	// Основная функция включения push-уведомлений
	async function enablePushNotifications() {
		if (!isPushSupported()) {
			alert("Push уведомления не поддерживаются этим браузером.");
			return false;
		}

		try {
			const registration = await navigator.serviceWorker.ready;

			let permission = Notification.permission;
			if (permission === 'default') {
				permission = await Notification.requestPermission();
			}

			if (permission !== 'granted') {
				alert("Для получения push-уведомлений необходимо разрешить уведомления в браузере.");
				return false;
			}

			const publicKey = await getVAPIDPublicKey();
			const subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(publicKey)
			});

			await fetch('/api/save-subscription', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ subscription })
			});

			alert("Push уведомления успешно включены!");
			return true;
		} catch (err) {
			alert("Ошибка при включении уведомлений: " + err.message);
			return false;
		}
	}

	// Функция отключения push-уведомлений
	/*async function disablePushNotifications() {
		try {
			const registration = await navigator.serviceWorker.getRegistration();
			if (!registration) return;

			const subscription = await registration.pushManager.getSubscription();
			if (subscription) {
				await subscription.unsubscribe();
				await fetch('/api/remove-subscription', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ endpoint: subscription.endpoint })
				});
				console.log("Push уведомления отключены.");
			}
		} catch (err) {
			console.error("Ошибка при отключении уведомлений:", err);
		}
	}*/

	// Модифицированный обработчик для push-уведомлений
	const handlePushNotificationChange = async (checked) => {
		if (checked) {
			// Проверяем, было ли уже запрошено разрешение
			const permissionRequested = localStorage.getItem('pushPermissionRequested');

			if (!permissionRequested) {
				// Показываем модальное окно при первом включении
				setShowPushModal(true);
			} else {
				// Если уже запрашивали, сразу пытаемся включить
				const success = await enablePushNotifications();
				if (success) {
					setNotifications(prev => ({ ...prev, push: true }));
					await saveNotifications();
				}
			}
		} else {
			// Выключаем уведомления
			//await disablePushNotifications();
			setNotifications(prev => ({ ...prev, push: false }));
			await saveNotifications();
		}
	};

	// Обработчики для модального окна
	const handleAllowPush = async () => {
		setShowPushModal(false);
		const success = await enablePushNotifications();
		if (success) {
			setNotifications(prev => ({ ...prev, push: true }));
			await saveNotifications();
		}
		localStorage.setItem('pushPermissionRequested', 'true');
	};

	const handleDenyPush = () => {
		setShowPushModal(false);
		setNotifications(prev => ({ ...prev, push: false }));
		localStorage.setItem('pushPermissionRequested', 'true');
	};

	useEffect(() => {
		// Проверяем статус push-уведомлений при загрузке
		async function checkPushSubscription() {
			if (!isPushSupported()) return;

			try {
				const registration = await navigator.serviceWorker.getRegistration();
				if (registration) {
					const subscription = await registration.pushManager.getSubscription();
					if (subscription && notifications.push === false) {
						setNotifications(prev => ({ ...prev, push: true }));
					} else if (!subscription && notifications.push === true) {
						setNotifications(prev => ({ ...prev, push: false }));
					}
				}
			} catch (error) {
				console.error('Ошибка проверки подписки:', error);
			}
		}

		checkPushSubscription();
	}, []);

	// Функция для загрузки настроек прокси
	const loadProxySettings = async () => {
		setIsLoadingProxySettings(true);
		try {
			const res = await fetch('/api/cdn-settings', {
				method: 'GET',
				credentials: 'include'
			});

			if (res.ok) {
				const data = await res.json();
				return data.data;
			}
		} catch (err) {
			console.error("Ошибка загрузки настроек прокси:", err);
		} finally {
			setIsLoadingProxySettings(false);
		}
		return null;
	};

	useEffect(() => {
		const interval = setInterval(onRefreshDomains, 5000); // Используем переданную функцию
		return () => clearInterval(interval);
	}, [onRefreshDomains]);

	const handleVerifyClick = async (domain) => {
		setIsLoadingVerification(true);
		try {
			const res = await fetch(`/api/verify-domain?id=${domain.id}`);
			if (res.ok) {
				const data = await res.json();
				setVerificationData({
					domain: domain.name,
					domainId: domain.id,
					txtRecord: data.data.verificationTxt || `ai-shield-verification=${data.data.token}`
				});
				setShowVerificationModal(true);
			} else {
				console.error("Ошибка получения токена верификации");
			}
		} catch (err) {
			console.error("Ошибка получения токена верификации:", err);
		}
		setIsLoadingVerification(false);
	};

	const handleSetupProxy = async (domain) => {
		const settings = await loadProxySettings();
		if (settings) {
			setProxySetupData({
				domain: domain.name,
				domainId: domain.id,
				nodes: settings.nodes || [],
				acmeCNAME: settings.acmeCNAME || ''
			});
			setShowProxySetupModal(true);
		}
	};

	const handleDeleteClick = (domain) => {
		setDomainToDelete(domain);
	};

	const confirmDelete = async () => {
		if (!domainToDelete) return;

		try {
			await onDeleteDomain(domainToDelete.id);
			setDomainToDelete(null);
		} catch (err) {
			console.error("Ошибка удаления домена:", err);
		}
	};

	const getDomainStatus = (domain) => {
		if (!domain.verified) {
			return { status: 'unverified', label: 'Ожидает проверки', color: 'yellow' };
		} else if (domain.verified && !domain.active) {
			return { status: 'verified', label: 'Подтвержден', color: 'yellow' };
		} else {
			return { status: 'active', label: 'Активен', color: 'green' };
		}
	};

	const loadCert = async (domain) => {
		setIsLoadingCerts(true);
		try {
			const res = await fetch(`/api/domain-certs/get?domain_id=${domain.id}`, {
				credentials: 'include'
			});

			if (res.ok) {
				const data = await res.json();
				const existingCert = data.data[0];

				setCertManagement({
					domainId: domain.id,
					domainName: domain.name,
					cert: existingCert?.cert || '',
					key: existingCert?.key || '',
					type: existingCert?.type || 'custom',
					mode: existingCert ? 'view' : 'upload'
				});
				setShowCertModal(true);
			}
		} catch (err) {
			console.error("Ошибка загрузки сертификата:", err);
		} finally {
			setIsLoadingCerts(false);
		}
	};

	// Сохранение сертификата
	const saveCert = async () => {
		if (!certManagement.cert.trim() || !certManagement.key.trim()) {
			alert('Заполните оба поля: сертификат и приватный ключ');
			return;
		}

		setIsLoadingCerts(true);
		try {
			const res = await fetch("/api/domain-certs/save", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					domain_id: certManagement.domainId,
					cert: certManagement.cert.trim(),
					key: certManagement.key.trim(),
					type: certManagement.type
				})
			});

			if (res.ok) {
				alert('Сертификат успешно сохранен!');
				setCertManagement(prev => ({ ...prev, mode: 'view' }));
				// Можно обновить список доменов если нужно показать статус SSL
				onRefreshDomains();
			} else {
				alert('Ошибка сохранения сертификата');
			}
		} catch (err) {
			console.error("Ошибка сохранения сертификата:", err);
			alert('Ошибка соединения с сервером');
		} finally {
			setIsLoadingCerts(false);
		}
	};

	// Удаление сертификата
	const deleteCert = async () => {
		if (!window.confirm('Вы уверены, что хотите удалить сертификат для этого домена?')) {
			return;
		}

		setIsLoadingCerts(true);
		try {
			const res = await fetch(`/api/domain-certs/delete`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					domain_id: certManagement.domainId
				})
			});

			if (res.ok) {
				alert('Сертификат удален!');
				setShowCertModal(false);
				onRefreshDomains();
			} else {
				alert('Ошибка удаления сертификата');
			}
		} catch (err) {
			console.error("Ошибка удаления сертификата:", err);
			alert('Ошибка соединения с сервером');
		} finally {
			setIsLoadingCerts(false);
		}
	};

	// Обработчики для загрузки файлов
	const handleCertFileUpload = (event, field) => {
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (e) => {
			setCertManagement(prev => ({
				...prev,
				[field]: e.target.result
			}));
		};
		reader.readAsText(file);
	};

	// Сброс формы
	const resetCertForm = () => {
		setCertManagement(prev => ({
			...prev,
			cert: '',
			key: '',
			mode: 'upload'
		}));
	};


	const handleChangePassword = async () => {
		if (newPassword !== confirmPassword) {
			alert("Новый пароль и подтверждение не совпадают");
			return;
		}

		setIsSaving(true);
		try {
			const res = await fetch("/api/ui/account/change-password", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					current_password: currentPassword,
					new_password: newPassword
				}),
			});

			const data = await res.json();
			if (res.ok && data.type === "success") {
				alert("Пароль успешно изменен!");
				setCurrentPassword('');
				setNewPassword('');
				setConfirmPassword('');
			} else {
				alert(data.message || "Ошибка смены пароля");
			}
		} catch (err) {
			console.error(err);
			alert("Ошибка соединения с сервером");
		} finally {
			setIsSaving(false);
		}
	};

	useEffect(() => {
		async function fetchSubscriptionInfo() {
			setIsLoadingSubscription(true);
			try {
				const res = await fetch("/api/ui/subscription/info", { credentials: "include" });
				if (res.ok) {
					const data = await res.json();
					if (data.type === "success") {
						setSubscriptionInfo({
							plan: data.data.plan,
							status: data.data.status,
							expiresAt: data.data.expiresAt,
							autoRenew: data.data.autoRenew,
							plans: data.data.plans || []
						});
					}
				}
			} catch (err) {
				console.error("Ошибка загрузки информации о подписке:", err);
			} finally {
				setIsLoadingSubscription(false);
			}
		}
		fetchSubscriptionInfo();
	}, []);


	const handlePlanChange = (newPlan) => {
		setSubscriptionInfo(prev => ({ ...prev, plan: newPlan }));
		// Здесь можно добавить дополнительную логику после успешной смены тарифа
	};

	const effectivePlanData = subscriptionInfo.effectivePlanData ||
		subscriptionInfo.plans.find(p => p.name === (subscriptionInfo.plan === "Test" ? "Full" : subscriptionInfo.plan));

	const handleRenewSubscription = () => {
		const newExpiryDate = new Date();
		newExpiryDate.setMonth(newExpiryDate.getMonth() + 1);
		setSubscriptionInfo(prev => ({
			...prev,
			expiresAt: newExpiryDate.toISOString().split('T')[0],
			status: 'active'
		}));
	};

	const getBasePlan = (planName) => {
		// Если тариф содержит дефис, берем часть до дефиса
		if (planName && planName.includes('-')) {
			return planName.split('-')[0];
		}
		return planName;
	};

	const getPlanColor = (planName) => {
		const basePlan = getBasePlan(planName);

		switch (basePlan) {
			case "Test": return "from-purple-500 to-pink-600";
			case "Base": return "from-yellow-500 to-amber-600";
			case "Pro": return "from-blue-500 to-purple-600";
			case "Full": return "from-green-500 to-emerald-600";
			default: return "from-gray-500 to-gray-600";
		}
	};

	// Обновленная функция getPlanIcon
	const getPlanIcon = (planName) => {
		const basePlan = getBasePlan(planName);

		switch (basePlan) {
			case "Test": return <FiStar className="w-5 h-5" />;
			case "Full": return <FaCrown className="w-5 h-5" />;
			case "Pro": return <FiZap className="w-5 h-5" />;
			case "Base": return <FiShield className="w-5 h-5" />;
			default: return <FiShield className="w-5 h-5" />;
		}
	};

	const getStatusInfo = (status) => {
		switch (status) {
			case "active": return { label: "Активна", color: "text-green-400", bg: "bg-green-500/20" };
			case "expired": return { label: "Истекла", color: "text-red-400", bg: "bg-red-500/20" };
			case "canceled": return { label: "Отменена", color: "text-yellow-400", bg: "bg-yellow-500/20" };
			default: return { label: "Неизвестно", color: "text-gray-400", bg: "bg-gray-500/20" };
		}
	};

	const getMaxDomains = (planName) => {
		const currentPlan = subscriptionInfo.plans.find(p => p.name === planName);

		if (currentPlan && currentPlan.domains_limit !== undefined) {
			return currentPlan.domains_limit;
		}

		if (planName === "Test" && subscriptionInfo.effectivePlanData) {
			return 1;
		}

		switch (planName) {
			case "Base": return 0;
			case "Pro": return 0;
			case "Full": return 0;
			default: return 0;
		}
	};

	const currentPlanData = subscriptionInfo.plans.find(p => p.name === subscriptionInfo.plan);
	const maxDomains = getMaxDomains(subscriptionInfo.plan);
	const statusInfo = getStatusInfo(subscriptionInfo.status);

	const handleDownloadArchive = async (domain) => {
		setDownloadingArchive(true);
		setArchiveDomainId(domain.id);

		try {
			const res = await fetch(`/api/domain-certs/download?domain_name=${encodeURIComponent(domain.name)}`, {
				credentials: 'include',
				method: 'GET'
			});

			if (res.ok) {
				// Получаем blob и создаем ссылку для скачивания
				const blob = await res.blob();
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = `certificates_${domain.name.replace(/\./g, '_')}.zip`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			} else {
				console.error('Ошибка при скачивании архива');
				alert('Не удалось скачать архив с сертификатами. Возможно, сертификаты еще не сгенерированы.');
			}
		} catch (err) {
			console.error('Ошибка при скачивании архива:', err);
			alert('Ошибка соединения с сервером');
		} finally {
			setDownloadingArchive(false);
			setArchiveDomainId(null);
		}
	};

	const filteredDomains = useMemo(() => {
		if (!domainSearch.trim()) return domains;
		const search = domainSearch.toLowerCase();
		return domains.filter(d => d.name.toLowerCase().includes(search));
	}, [domains, domainSearch]);

	return (
		<div className="space-y-8">
			{/* Модальное окно подтверждения удаления */}
			<AnimatePresence>
				{domainToDelete && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
						onClick={() => setDomainToDelete(null)}
					>
						<motion.div
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-xl font-semibold">Удаление домена</h3>
								<button
									onClick={() => setDomainToDelete(null)}
									className="p-1 hover:bg-white/10 rounded-lg"
								>
									<FiX size={24} />
								</button>
							</div>

							<div className="space-y-4">
								<p>Вы уверены, что хотите удалить домен <strong>{domainToDelete.name}</strong>?</p>
								<p className="text-sm text-gray-400">Это действие нельзя отменить.</p>

								<div className="flex gap-3">
									<button
										onClick={() => setDomainToDelete(null)}
										className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
									>
										Отмена
									</button>
									<button
										onClick={confirmDelete}
										className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all"
									>
										Удалить
									</button>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Модальное окно верификации домена */}
			<VerificationModal
				isOpen={showVerificationModal}
				onClose={() => setShowVerificationModal(false)}
				domain={verificationData.domain}
				domainId={verificationData.domainId}
				txtRecord={verificationData.txtRecord}
				onVerifyDomain={onVerifyDomain}
			/>

			{/* Модальное окно настройки прокси */}
			<ProxySetupModal
				isOpen={showProxySetupModal}
				onClose={() => setShowProxySetupModal(false)}
				domain={proxySetupData.domain}
				nodes={proxySetupData.nodes}
				acmeCNAME={proxySetupData.acmeCNAME}
				isLoading={isLoadingProxySettings}
			/>

			{/* Модальные окна для управления подпиской */}
			<PlanSelectionModal
				isOpen={showPlanModal}
				onClose={() => setShowPlanModal(false)}
				currentPlan={subscriptionInfo.plan}
				onPlanChange={handlePlanChange}
				plans={subscriptionInfo.plans}
				isLoading={isLoadingSubscription}
			/>

			<RenewSubscriptionModal
				isOpen={showRenewModal}
				onClose={() => setShowRenewModal(false)}
				currentPlan={subscriptionInfo.plan}
				onRenew={handleRenewSubscription}
				plans={subscriptionInfo.plans}
				isLoading={isLoadingSubscription}
			/>

			{/* Остальной код */}
			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<h3 className="text-lg font-semibold mb-6">Информация об аккаунте</h3>

				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<span className="text-gray-400">Email</span>
						<span>{accountInfo.email || '—'}</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-gray-400">Дата регистрации</span>
						<span>{accountInfo.createdAt ? new Date(accountInfo.createdAt).toLocaleDateString() : '—'}</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-gray-400">Телеграм</span>
						{accountInfo.telegram_connected ? (
							<span className="text-green-400">Подключен</span>
						) : (
							<a
								href="https://t.me/shield_ai_bot"
								target="_blank"
								rel="noreferrer"
								className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
							>
								Подключить
							</a>
						)}
					</div>

					<div className="flex items-center justify-between">
						<span className="text-gray-400">Тарифный план</span>
						<div className="flex items-center gap-2">
							{getPlanIcon(subscriptionInfo.plan)}
							<span className="font-medium">
								{subscriptionInfo.plan}
								{subscriptionInfo.plan === "Test" && " (пробный период)"}
							</span>
						</div>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-gray-400">Статус подписки</span>
						<span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
							{statusInfo.label}
						</span>
					</div>
				</div>
			</div>

			{/* Секция управления подпиской */}
			<div id="subscription" className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
					<div>
						<h3 className="text-lg font-semibold mb-2">Управление подпиской</h3>
						<p className="text-gray-400 text-sm">
							Информация о вашем текущем тарифе и управление подпиской
						</p>
					</div>
					<div className="flex gap-2">
						{subscriptionInfo.status === 'active' && (
							<button
								onClick={() => setShowRenewModal(true)}
								className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-500 hover:to-green-600 rounded-lg transition-all"
							>
								<FiCreditCard className="w-4 h-4" />
								Продлить
							</button>
						)}
						<button
							onClick={() => setShowPlanModal(true)}
							disabled={isLoadingSubscription}
							className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-blue-500 rounded-lg transition-all disabled:opacity-50"
						>
							<FaCrown className="w-4 h-4" />
							{subscriptionInfo.status === 'inactive' ? 'Выбрать тариф' : 'Сменить тариф'}
						</button>
					</div>
				</div>

				{isLoadingSubscription ? (
					<div className="flex justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : (
					<>
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							{/* Карточка текущего тарифа */}
							<div className={`bg-gradient-to-r ${getPlanColor(subscriptionInfo.plan)} rounded-xl p-6 text-white`}>
								<div className="flex items-center justify-between mb-4">
									<div className="flex items-center gap-3">
										{getPlanIcon(subscriptionInfo.plan)}
										<span className="text-xl font-bold">
											{subscriptionInfo.plan}
											{subscriptionInfo.plan === "Test" && " (пробный)"}
										</span>
									</div>
									<span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.color}`}>
										{statusInfo.label}
									</span>
								</div>

								{subscriptionInfo.status !== "inactive" && subscriptionInfo.plan !== "Нет подписки" && (
									<div className="text-3xl font-bold mb-2">
										{subscriptionInfo.plan === "Test" ? "Бесплатно" : `${effectivePlanData?.price}₽/мес`}
									</div>
								)}

								{subscriptionInfo.expiresAt ? (
									<div className="flex items-center gap-2 text-white/80">
										<FiCalendar className="w-4 h-4" />
										<span>Действует до: {new Date(subscriptionInfo.expiresAt).toLocaleDateString('ru-RU')}</span>
									</div>
								) : (
									<div className="text-white/80">Подписка не активна</div>
								)}
							</div>

							{/* Информация о возможностях тарифа */}
							<div className="bg-white/5 rounded-xl p-6">
								<h4 className="font-semibold mb-4">Возможности вашего тарифа</h4>
								{effectivePlanData ? (
									<ul className="space-y-3">
										{effectivePlanData.features.slice(0, 4).map((feature, index) => (
											<li key={index} className="flex items-center gap-3 text-sm">
												<FiCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
												<span className="text-gray-300">{feature}</span>
											</li>
										))}
										{subscriptionInfo.plan === "Test" && (
											<li className="flex items-center gap-3 text-sm text-yellow-400">
												<FiClock className="w-4 h-4 flex-shrink-0" />
												<span>Пробный период действует 7 дней</span>
											</li>
										)}
									</ul>
								) : (
									<p className="text-gray-400 text-sm">Информация о тарифе недоступна</p>
								)}
							</div>
						</div>

						{/* Прогресс использования доменов */}
						{subscriptionInfo.status !== 'inactive' && maxDomains > 0 && (
							<div className="mt-6 pt-6 border-t border-white/10">
								<div className="flex justify-between items-center mb-3">
									<span className="text-gray-400">Использование доменов</span>
									<span className="text-sm text-gray-400">
										{domains.length} из {maxDomains}
									</span>
								</div>
								<div className="w-full bg-gray-700 rounded-full h-2.5">
									<div
										className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-500"
										style={{
											width: maxDomains === Infinity ? '100%' :
												`${Math.min((domains.length / maxDomains) * 100, 100)}%`
										}}
									></div>
								</div>
							</div>
						)}
					</>
				)}
			</div>

			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
					<h3 className="text-lg font-semibold">Управление доменами</h3>
					<button
						onClick={onAddDomain}
						className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-all w-full sm:w-auto justify-center"
					>
						<FiPlus />
						Добавить домен
					</button>
				</div>
				<div className="relative mb-6">
					<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
					<input
						type="text"
						name="domain-search"
						autoComplete="off"
						placeholder="Поиск домена..."
						value={domainSearch}
						onChange={e => setDomainSearch(e.target.value)}
						className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
					/>
				</div>
				{/* Добавленная ссылка на документацию */}
				<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
					<div className="flex items-start gap-3">
						<FiInfo className="w-5 h-5 text-blue-400 mt-0.5" />
						<div className="text-sm">
							<p className="text-gray-300">
								Не уверены как подключить домен? Ознакомьтесь с{' '}
								<a
									href="/docs#setup"
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-400 hover:text-blue-300 underline"
								>
									подробной инструкцией
								</a>
								, где описаны все шаги подключения.
							</p>
						</div>
					</div>
				</div>

				{/* Дополнительная информация о сертификатах */}
				<div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
					<div className="flex items-start gap-3">
						<FiDownload className="w-5 h-5 text-green-400 mt-0.5" />
						<div className="text-sm">
							<h4 className="font-medium text-green-300 mb-1">Сертификаты для вашего бекенда</h4>
							<p className="text-gray-300">
								Для защиты соединения между нашими серверами и вашим бекендом, вы можете скачать архив с
								SSL сертификатами. Эти сертификаты нужно установить на вашем сервере для безопасного
								HTTPS-соединения.
							</p>
						</div>
					</div>
				</div>

				<div className="space-y-4">
					{filteredDomains.map(domain => {
						const status = getDomainStatus(domain);

						return (
							<div key={domain.id} className="flex flex-col lg:flex-row lg:justify-between lg:items-center p-4 bg-white/5 rounded-lg gap-4">
								<div className="flex-1 min-w-0">
									<h4 className="font-medium truncate">{domain.name}</h4>
									<p className="text-sm text-gray-400 truncate">Бекенд: {domain.backendIP}</p>
									{domain.last_verified_at && (
										<p className="text-xs text-gray-500">
											Проверен: {new Date(domain.last_verified_at).toLocaleDateString()}
										</p>
									)}
								</div>
								<div className="flex flex-wrap gap-2 justify-start lg:justify-end">
									<span className={`${status.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'} px-3 py-1 rounded-full text-sm whitespace-nowrap`}>
										{status.label}
									</span>

									{status.status === 'unverified' && (
										<button
											onClick={() => handleVerifyClick(domain)}
											disabled={isLoadingVerification}
											className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg text-sm transition-all whitespace-nowrap"
										>
											{isLoadingVerification ? (
												<div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
											) : (
												<>
													<FiCheckCircle size={14} />
													Подтвердить
												</>
											)}
										</button>
									)}

									{status.status === 'verified' && (
										<button
											onClick={() => handleSetupProxy(domain)}
											className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-all whitespace-nowrap"
										>
											<FiSettings size={14} />
											Настроить прокси
										</button>
									)}

									{status.status === 'active' && (
										<>
											<button
												onClick={() => loadCert(domain)}
												className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition-all whitespace-nowrap"
											>
												<FiLock size={14} />
												SSL
											</button>

											{/* Новая кнопка для скачивания архива с сертификатами */}
											<button
												onClick={() => handleDownloadArchive(domain)}
												disabled={downloadingArchive && archiveDomainId === domain.id}
												className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-emerald-500 hover:to-green-500 rounded-lg text-sm transition-all whitespace-nowrap relative group"
												title="Скачать архив с SSL сертификатами для установки на ваш бекенд"
											>
												{downloadingArchive && archiveDomainId === domain.id ? (
													<div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
												) : (
													<>
														<FiDownload size={14} />
														Сертификаты
													</>
												)}

												{/* Всплывающая подсказка */}
												<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
													Скачать архив с SSL сертификатами для вашего бекенда
													<div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
												</div>
											</button>
										</>
									)}

									<button
										onClick={() => handleDeleteClick(domain)}
										className="flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 rounded-lg text-sm transition-all whitespace-nowrap"
									>
										<FiX size={14} />
										Удалить
									</button>
								</div>
							</div>
						);
					})}
					{filteredDomains.length === 0 && domains.length > 0 && (
						<div className="text-center py-4 text-gray-400">Домены не найдены</div>
					)}
				</div>
			</div>

			{/* Настройки уведомлений */}
			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<h3 className="text-lg font-semibold mb-6">Настройки уведомлений</h3>

				<div className="space-y-6">
					<div className="flex items-center justify-between">
						<div>
							<h4 className="font-medium">Push-уведомления</h4>
							<p className="text-sm text-gray-400">Получайте уведомления в браузере</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								className="sr-only peer"
								checked={notifications.push}
								onChange={(e) => handlePushNotificationChange(e.target.checked)}
							/>
							<div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
						</label>
					</div>

					<div className="flex items-center justify-between">
						<div>
							<h4 className="font-medium">Telegram-уведомления</h4>
							<p className="text-sm text-gray-400">
								Получайте уведомления в телеграм
							</p>
						</div>
						{accountInfo.telegram_connected ? (
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									className="sr-only peer"
									checked={notifications.telegram}
									onChange={() => handleNotificationChange('telegram')}
								/>
								<div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
							</label>
						) : (
							<a
								href="https://t.me/shield_ai_bot"
								target="_blank"
								rel="noreferrer"
								className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
							>
								Подключить
							</a>
						)}
					</div>

					<div className="flex items-center justify-between">
						<div>
							<h4 className="font-medium">Email-уведомления</h4>
							<p className="text-sm text-gray-400">Получайте уведомления на почту</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								className="sr-only peer"
								checked={notifications.email}
								onChange={() => handleNotificationChange('email')}
							/>
							<div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
						</label>
					</div>

					<div className="flex justify-end space-x-4">
						<button
							onClick={resetNotifications}
							className="px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
						>
							Отменить
						</button>
						<button
							onClick={saveNotifications}
							className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
						>
							Применить
						</button>
					</div>
				</div>
			</div>

			<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
				<h3 className="text-lg font-semibold mb-6">Смена пароля</h3>

				<div className="space-y-4">
					<input
						type="text"
						name="username"
						autoComplete="username"
						style={{ display: 'none' }}
					/>

					<div>
						<label className="block text-sm font-medium text-gray-400 mb-1">Текущий пароль</label>
						<input
							type="password"
							className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="••••••••"
							value={currentPassword}
							onChange={e => setCurrentPassword(e.target.value)}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-400 mb-1">Новый пароль</label>
						<input
							type="password"
							className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="••••••••"
							value={newPassword}
							onChange={e => setNewPassword(e.target.value)}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-400 mb-1">Подтвердите пароль</label>
						<input
							type="password"
							className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="••••••••"
							value={confirmPassword}
							onChange={e => setConfirmPassword(e.target.value)}
						/>
					</div>

					<button
						onClick={handleChangePassword}
						disabled={isSaving}
						className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
					>
						{isSaving ? "Сохраняем..." : "Сохранить изменения"}
					</button>
				</div>
			</div>
			<AnimatePresence>
				{showCertModal && (
					<motion.div
						className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => setShowCertModal(false)}
					>
						<motion.div
							className="bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-center mb-6">
								<h3 className="text-xl font-semibold">
									SSL сертификат для {certManagement.domainName}
								</h3>
								<button
									onClick={() => setShowCertModal(false)}
									className="p-2 hover:bg-white/10 rounded-lg transition-colors"
								>
									<FiX size={20} />
								</button>
							</div>

							{isLoadingCerts ? (
								<div className="flex justify-center py-8">
									<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
								</div>
							) : (
								<div className="space-y-6">
									{/* Режим просмотра существующего сертификата */}
									{certManagement.mode === 'view' && certManagement.cert && (
										<div className="space-y-4">
											<div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
												<div className="flex items-center gap-2 text-green-400">
													<FiCheckCircle size={20} />
													<span className="font-medium">Сертификат настроен</span>
												</div>
												<p className="text-sm text-gray-300 mt-2">
													SSL сертификат активен для этого домена
												</p>
											</div>

											<div className="flex gap-3">
												<button
													onClick={() => setCertManagement(prev => ({ ...prev, mode: 'edit' }))}
													className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
												>
													<FiEdit3 size={16} />
													Редактировать
												</button>
												<button
													onClick={deleteCert}
													className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
												>
													<FiTrash2 size={16} />
													Удалить
												</button>
											</div>
										</div>
									)}

									{/* Режим редактирования/загрузки */}
									{(certManagement.mode === 'edit' || certManagement.mode === 'upload') && (
										<div className="space-y-4">
											<div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-4">
												<div className="flex items-start gap-3">
													<FiInfo className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
													<div>
														<p className="font-medium text-blue-300 mb-1">Используется Let's Encrypt по умолчанию</p>
														<p className="text-sm text-gray-300">
															Ваш домен уже защищен автоматическим SSL сертификатом от Let's Encrypt.
															Загружайте свой сертификат только если вам нужен:
														</p>
														<ul className="text-sm text-gray-300 mt-2 space-y-1">
															<li>• SSL от другого провайдера</li>
															<li>• Extended Validation (EV) сертификат</li>
															<li>• Wildcard сертификат (если не используете Let's Encrypt)</li>
														</ul>
													</div>
												</div>
											</div>
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
												<div>
													<label className="block text-sm font-medium text-gray-400 mb-2">
														Сертификат (PEM)
													</label>
													<div className="flex gap-2">
														<input
															type="file"
															accept=".pem,.crt,.cer"
															onChange={(e) => handleCertFileUpload(e, 'cert')}
															className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700"
														/>
													</div>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-400 mb-2">
														Приватный ключ (PEM)
													</label>
													<div className="flex gap-2">
														<input
															type="file"
															accept=".pem,.key"
															onChange={(e) => handleCertFileUpload(e, 'key')}
															className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700"
														/>
													</div>
												</div>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div>
													<label className="block text-sm font-medium text-gray-400 mb-2">
														Сертификат (PEM формат)
													</label>
													<textarea
														placeholder="-----BEGIN CERTIFICATE----- ..."
														className="w-full h-32 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
														value={certManagement.cert}
														onChange={(e) => setCertManagement(prev => ({ ...prev, cert: e.target.value }))}
													/>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-400 mb-2">
														Приватный ключ (PEM формат)
													</label>
													<textarea
														placeholder="-----BEGIN PRIVATE KEY----- ..."
														className="w-full h-32 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
														value={certManagement.key}
														onChange={(e) => setCertManagement(prev => ({ ...prev, key: e.target.value }))}
													/>
												</div>
											</div>

											<div className="flex gap-3 pt-4">
												<button
													onClick={saveCert}
													disabled={isLoadingCerts}
													className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg transition-colors"
												>
													{isLoadingCerts ? (
														<div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
													) : (
														<FiSave size={16} />
													)}
													Сохранить
												</button>

												<button
													onClick={resetCertForm}
													className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
												>
													<FiRefreshCw size={16} />
													Очистить
												</button>

												{certManagement.mode === 'edit' && (
													<button
														onClick={() => setCertManagement(prev => ({ ...prev, mode: 'view' }))}
														className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors ml-auto"
													>
														Отмена
													</button>
												)}
											</div>
										</div>
									)}

									{/* Подсказки */}
									<div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
										<h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
											<FiInfo size={16} />
											Информация о сертификатах
										</h4>
										<ul className="text-sm text-gray-300 space-y-1">
											<li>• <strong>По умолчанию</strong> используется автоматический SSL сертификат от Let's Encrypt</li>
											<li>• Загрузите свой сертификат только если нужен кастомный SSL</li>
											<li>• Можно загрузить файлы или вставить текст в PEM формате</li>
											<li>• Сертификат будет применен сразу после сохранения</li>
										</ul>
									</div>
								</div>
							)}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
			{/* Модальное окно для push-уведомлений */}
			<AnimatePresence>
				{showPushModal && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
						onClick={() => setShowPushModal(false)}
					>
						<motion.div
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-xl font-semibold">Push-уведомления</h3>
								<button
									onClick={() => setShowPushModal(false)}
									className="p-1 hover:bg-white/10 rounded-lg"
								>
									<FiX size={24} />
								</button>
							</div>

							<div className="space-y-4">
								<p>Разрешить отправку push-уведомлений? Вы будете получать уведомления о важных событиях даже когда сайт не открыт.</p>

								<div className="flex gap-3">
									<button
										onClick={handleDenyPush}
										className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
									>
										Не сейчас
									</button>
									<button
										onClick={handleAllowPush}
										className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
									>
										Разрешить
									</button>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

const PlanSelectionModal = ({ isOpen, onClose, currentPlan, plans, isLoading }) => {
	const [selectedPlan, setSelectedPlan] = useState(currentPlan);
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	// Фильтруем планы: скрываем Test, если текущий тариф не Test
	const filteredPlans = plans.filter(plan => {
		if (currentPlan !== "Test" && plan.name === "Test") {
			return false;
		}
		return true;
	});

	const handlePlanSelect = (planName) => {
		if (planName === currentPlan) return;

		setSelectedPlan(planName);
		setShowConfirmModal(true);
	};

	if (!isOpen) return null;

	const isTestPlan = currentPlan === "Test";

	return (
		<>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
				onClick={onClose}
			>
				<motion.div
					initial={{ scale: 0.9, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					exit={{ scale: 0.9, opacity: 0 }}
					className="bg-gray-800 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
					onClick={e => e.stopPropagation()}
				>
					<div className="flex justify-between items-center mb-6">
						<div>
							<h3 className="text-2xl font-bold">Выбор тарифного плана</h3>
							{isTestPlan && (
								<p className="text-yellow-400 text-sm mt-1">
									Ваш пробный период скоро закончится. Выберите тариф для продолжения использования.
								</p>
							)}
						</div>
					</div>

					{isLoading ? (
						<div className="flex justify-center py-12">
							<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
						</div>
					) : (
						<>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
								{filteredPlans.map((plan, index) => (
									<div
										key={index}
										className={`relative bg-white/5 rounded-2xl p-6 border transition-all duration-300 ${plan.name === currentPlan
											? 'border-purple-500 shadow-2xl shadow-purple-500/20'
											: plan.popular
												? 'border-blue-500/50'
												: 'border-white/10'
											} ${plan.name !== currentPlan ? 'hover:scale-105 hover:border-blue-500/80 cursor-pointer' : ''
											}`}
										onClick={() => handlePlanSelect(plan.name)}
									>
										{plan.popular && (
											<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
												<span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
													<FiStar size={12} />
													Популярный
												</span>
											</div>
										)}

										{plan.beta && plan.name !== currentPlan && !plan.popular && (
											<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
												<span className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
													<FiZap size={12} />
													Beta
												</span>
											</div>
										)}

										{plan.name === currentPlan && (
											<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
												<span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
													<FiCheck size={12} />
													Текущий
												</span>
											</div>
										)}

										<div className="text-center mb-6">
											<div className="flex items-center justify-center gap-2 mb-2">
												{plan.name === "Base" && <FaCrown className="text-yellow-400" />}
												{plan.name === "Pro" && <FiZap className="text-blue-400" />}
												{plan.name === "Full" && <FiShield className="text-green-400" />}
												<h4 className="text-xl font-bold">{plan.name}</h4>
											</div>
											<div className="text-3xl font-extrabold mb-2">{plan.price}₽/мес</div>
											<p className="text-gray-400 text-sm">{plan.description}</p>
										</div>

										<ul className="space-y-3 mb-6">
											{plan.features.map((feature, idx) => (
												<li key={idx} className="flex items-center gap-3 text-sm">
													<FiCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
													<span>{feature}</span>
												</li>
											))}
										</ul>

										{plan.name === currentPlan ? (
											<button
												disabled
												className="w-full py-3 bg-gray-600 text-gray-400 rounded-xl font-semibold cursor-not-allowed"
											>
												Текущий план
											</button>
										) : (
											<button
												onClick={() => handlePlanSelect(plan.name)}
												className="w-full py-3 rounded-xl font-semibold transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-purple-500 hover:to-blue-500"
											>
												Выбрать тариф
											</button>
										)}
									</div>
								))}
							</div>

							{/* Если у пользователя не Test тариф, показываем сообщение о том, что Test недоступен */}
							{currentPlan !== "Test" && (
								<div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-4">
									<h4 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
										<FiInfo size={16} />
										Пробный период недоступен
									</h4>
									<p className="text-sm text-gray-300">
										Тариф "Test" доступен только для новых пользователей. Вы уже используете платный тариф и не можете вернуться к пробной версии.
									</p>
								</div>
							)}

							<div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-4">
								<h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
									<FiZap size={16} />
									О тарифе Beta
								</h4>
								<p className="text-sm text-gray-300">
									Тариф "Full" находится в стадии Beta-тестирования. Вы получите доступ к самым современным функциям защиты,
									а также возможность напрямую влиять на развитие продукта через обратную связь.
								</p>
							</div>

							<div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
								<h4 className="font-semibold text-blue-400 mb-2">Важная информация</h4>
								<ul className="text-sm text-gray-300 space-y-1">
									<li>• При смене тарифа разница будет зачтена в счет следующего платежа</li>
									<li>• Изменения вступают в силу после оплаты</li>
									<li>• Вы можете изменить тариф в любой момент</li>
								</ul>
							</div>
						</>
					)}
				</motion.div>
			</motion.div>

			{/* Модальное окно подтверждения смены тарифа */}
			<ConfirmPlanChangeModal
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				currentPlan={currentPlan}
				newPlan={selectedPlan}
				plans={plans}
			/>
		</>
	);
};

const ConfirmPlanChangeModal = ({ isOpen, onClose, currentPlan, newPlan, plans }) => {
	if (!isOpen) return null;

	const newPlanData = plans.find(plan => plan.name === newPlan);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
			onClick={onClose}
		>
			<motion.div
				initial={{ scale: 0.9, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				exit={{ scale: 0.9, opacity: 0 }}
				className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
				onClick={e => e.stopPropagation()}
			>
				<div className="flex justify-between items-center mb-4">
					<h3 className="text-xl font-semibold">Подтверждение смены тарифа</h3>
					<button
						onClick={onClose}
						className="p-1 hover:bg-white/10 rounded-lg"
					>
						<FiX size={24} />
					</button>
				</div>

				<div className="space-y-4">
					<div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
						<div className="flex items-center gap-3 mb-2">
							<FiInfo className="text-blue-400" size={20} />
							<span className="font-semibold">Важная информация</span>
						</div>
						<p className="text-sm text-gray-300">
							При переходе на более дорогой тариф будет учтена стоимость неиспользованного времени текущего тарифа.
						</p>
					</div>

					<div className="bg-white/5 rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<span className="text-gray-400">Текущий тариф:</span>
							<span className="font-medium">{currentPlan}</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-gray-400">Новый тариф:</span>
							<span className="font-medium text-blue-400">{newPlan}</span>
						</div>
						<div className="flex items-center justify-between mt-2">
							<span className="text-gray-400">Стоимость:</span>
							<span className="text-lg font-bold">{newPlanData?.price}₽/мес</span>
						</div>
					</div>

					<p className="text-gray-300 text-sm">
						После подтверждения вы будете перенаправлены на страницу оплаты.
					</p>

					<div className="flex gap-3">
						<button
							onClick={onClose}
							className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
						>
							Отмена
						</button>
						<a
							href={`/purchase?rate=${encodeURIComponent(newPlan)}`}
							className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-500 hover:to-green-600 rounded-lg transition-all text-center font-semibold"
						>
							Выбрать
						</a>
					</div>
				</div>
			</motion.div>
		</motion.div>
	);
};

// Компонент модального окна продления подписки
const RenewSubscriptionModal = ({ isOpen, onClose, currentPlan, plans }) => {
	if (!isOpen) return null;

	// Для тестовой подписки используем Full тариф
	const displayPlan = currentPlan === "Test" ? "Full" : currentPlan;
	const currentPlanData = plans.find(plan => plan.name === displayPlan);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
			onClick={onClose}
		>
			<motion.div
				initial={{ scale: 0.9, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				exit={{ scale: 0.9, opacity: 0 }}
				className="bg-gray-800 rounded-2xl p-6 w-full max-w-md"
				onClick={e => e.stopPropagation()}
			>
				<div className="flex justify-between items-center mb-4">
					<h3 className="text-xl font-semibold">
						{currentPlan === "Test" ? "Активация полной версии" : "Продление подписки"}
					</h3>
					<button
						onClick={onClose}
						className="p-1 hover:bg-white/10 rounded-lg"
					>
						<FiX size={24} />
					</button>
				</div>

				<div className="space-y-4">
					<div className="bg-white/5 rounded-lg p-4">
						<div className="flex items-center gap-3 mb-2">
							{currentPlanData?.name === "Base" && <FaCrown className="text-yellow-400" />}
							{currentPlanData?.name === "Pro" && <FiZap className="text-blue-400" />}
							{currentPlanData?.name === "Full" && <FiShield className="text-green-400" />}
							<span className="font-semibold">{currentPlanData?.name}</span>
						</div>
						<div className="text-2xl font-bold">{currentPlanData?.price}₽/мес</div>
						{currentPlan === "Test" && (
							<p className="text-sm text-green-400 mt-1">
								После окончания пробного периода
							</p>
						)}
					</div>

					<p className="text-gray-300 text-sm">
						{currentPlan === "Test"
							? "Ваш пробный период скоро закончится. Активируйте полную версию для продолжения использования сервиса."
							: "Подписка будет продлена на 30 дней."
						}
					</p>

					<div className="flex gap-3">
						<button
							onClick={onClose}
							className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all"
						>
							Отмена
						</button>
						<a
							href={`/purchase?rate=${encodeURIComponent(displayPlan)}`}
							className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-500 hover:to-green-600 rounded-lg transition-all text-center font-semibold"
						>
							{currentPlan === "Test" ? "Активировать" : "Продлить"}
						</a>
					</div>
				</div>
			</motion.div>
		</motion.div>
	);
};

function StatCard({ title, value, change, icon, loading }) {
	return (
		<motion.div
			className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl"
			whileHover={{ y: -5 }}
		>
			<div className="flex justify-between items-start">
				<div>
					<p className="text-gray-400 text-sm">{title}</p>
					{loading ? (
						<div className="h-8 w-3/4 bg-white/10 rounded mt-2 animate-pulse"></div>
					) : (
						<p className="text-2xl font-bold mt-1">{value}</p>
					)}
				</div>
				<div className="p-2 bg-white/10 rounded-lg">{icon}</div>
			</div>
			{!loading && change !== undefined && (
				<p className={`mt-2 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
					{change >= 0 ? '↑' : '↓'} {Math.abs(change)}% за последний час
				</p>
			)}
		</motion.div>
	);
}

function getThreatClass(threat) {
	if (threat.includes('DDoS')) return 'bg-red-400/20 text-red-400';
	if (threat.includes('Brute')) return 'bg-purple-400/20 text-purple-400';
	if (threat.includes('Scanner')) return 'bg-yellow-400/20 text-yellow-400';
	return 'bg-gray-400/20 text-gray-400';
}

function getThreatLevelColor(level) {
	if (level > 80) return 'bg-red-500';
	if (level > 50) return 'bg-yellow-500';
	return 'bg-blue-500';
}

function isValidIP(ip) {
	return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
}
